import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowLeft, Star, XCircle, Send, UserCheck, FileDown, Download,
  CheckSquare, Square, FileText, Pencil, AlertCircle, CheckCircle,
  Clock, Mail, Activity, Eye, ExternalLink, ChevronDown, ChevronRight, ChevronLeft,
  Upload, Trash2, Tag, Plus, User, Shield, BookOpen, Users, BarChart2,
  RefreshCw, AlertTriangle, ShieldAlert, ShieldCheck, Loader2, HelpCircle, Flag, Ban
} from 'lucide-react';
import { downloadFileWithCorrectType, fetchFileAsObjectUrl, isFileKey, generateApplicationPDFWithLibrary } from '../utils/fileHelpers';
import ApplicationStagePath from '../components/ApplicationStagePath';

// Default checklist used as fallback while config loads
const DEFAULT_CHECKLIST_SECTIONS = [
  { id: 'personal_info', label: 'Personal Information', doc_idx: 0, items: [
    { key: 'id_verified',      label: 'Name on ID matches application' },
    { key: 'id_age_confirmed', label: 'Age ≤ 27 confirmed from ID' },
  ]},
  { id: 'o_level', label: 'O-Level Results', doc_idx: 1, items: [
    { key: 'o_level_cert',     label: 'Certificate is authentic' },
    { key: 'o_level_division', label: 'Division matches declared' },
  ]},
  { id: 'a_level_llb', label: 'A-Level / LLB', doc_idx: 2, items: [
    { key: 'a_level_cert',    label: 'Division 1 confirmed (A-Level)' },
    { key: 'llb_cert',        label: 'GPA ≥ 3.3 confirmed (LLB)' },
  ]},
  { id: 'declaration', label: 'Declarations', doc_idx: null, items: [
    { key: 'declarations_confirmed', label: 'All declarations confirmed' },
  ]},
  { id: 'referees', label: 'Referees', doc_idx: null, items: [
    { key: 'referee1_verified', label: 'Referee 1 contacts verified' },
    { key: 'referee2_verified', label: 'Referee 2 contacts verified' },
  ]},
  { id: 'general', label: 'General', doc_idx: null, items: [
    { key: 'personal_statement',  label: 'Personal statement reviewed' },
    { key: 'no_inconsistencies',  label: 'No inconsistencies found across the application' },
  ]},
];

const STATUS_COLORS = {
  received:        'bg-blue-100 text-blue-800 border-blue-200',
  ineligible:      'bg-red-100 text-red-700 border-red-200',
  shortlisted:     'bg-yellow-100 text-yellow-800 border-yellow-200',
  not_shortlisted: 'bg-gray-100 text-gray-600 border-gray-200',
  test_invited:    'bg-purple-100 text-purple-800 border-purple-200',
  test_completed:  'bg-indigo-100 text-indigo-800 border-indigo-200',
  accepted:        'bg-green-100 text-green-800 border-green-200',
  selected_tba:    'bg-violet-100 text-violet-800 border-violet-200',
  not_selected:    'bg-red-100 text-red-700 border-red-200',
  on_hold:         'bg-amber-100 text-amber-700 border-amber-200',
  enrolled:        'bg-teal-100 text-teal-800 border-teal-200',
  // legacy
  pending:         'bg-yellow-100 text-yellow-800 border-yellow-200',
  screening:       'bg-blue-100 text-blue-800 border-blue-200',
  test_sent:       'bg-purple-100 text-purple-800 border-purple-200',
  rejected:        'bg-red-100 text-red-700 border-red-200',
};

const STATUS_LABELS = {
  received:        'Received',
  ineligible:      'Ineligible',
  shortlisted:     'Shortlisted',
  not_shortlisted: 'Not Shortlisted',
  test_invited:    'Assessment Invited',
  test_completed:  'Assessment Completed',
  accepted:        'Accepted',
  selected_tba:    'Selected — Dates TBA',
  not_selected:    'Not Selected',
  on_hold:         'On Hold',
  enrolled:        'Enrolled',
  // legacy
  pending:         'Pending',
  screening:       'Screening',
  test_sent:       'Test Sent',
  rejected:        'Rejected',
};

const scoreColor = (s) => {
  if (s == null) return 'text-gray-500';
  if (s >= 70) return 'text-green-700';
  if (s >= 50) return 'text-yellow-700';
  return 'text-red-600';
};

const scoreBg = (s) => {
  if (s == null) return 'bg-gray-100 border-gray-200';
  if (s >= 70) return 'bg-green-50 border-green-300';
  if (s >= 50) return 'bg-yellow-50 border-yellow-300';
  return 'bg-red-50 border-red-300';
};

const fmtDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
  } catch { return null; }
};

// Format date+time as "09-Apr-2026 14:30"
const fmtDateTime = (d) => {
  if (!d) return null;
  try {
    const date = new Date(d);
    const datePart = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
    const timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  } catch { return null; }
};

function buildTimeline(app, events, emailLogs) {
  if (!app) return [];
  const findEv = (type, filter) => events.find(e => e.event_type === type && (!filter || filter(e.details)));
  const findEmail = (...keys) => emailLogs.find(e => keys.includes(e.template_key) && e.status === 'sent');

  const submittedEv = findEv('submitted');
  const shortlistedEv = findEv('shortlisted', d => d?.shortlisted === true);
  const declinedEv = findEv('shortlisted', d => d?.shortlisted === false);
  const toScreeningEv = findEv('status_change', d => ['screening', 'shortlisted'].includes(d?.to));
  const toTestSentEv = findEv('status_change', d => ['test_sent', 'test_invited'].includes(d?.to));
  const toTestDoneEv = findEv('status_change', d => d?.to === 'test_completed');
  const toAcceptedEv = findEv('status_change', d => d?.to === 'accepted');
  const toRejectedEv = findEv('status_change', d => d?.to === 'rejected');
  const toEnrolledEv = findEv('status_change', d => d?.to === 'enrolled');

  const emailShortlist = findEmail('shortlisted');
  const emailNotShortlist = findEmail('not_shortlisted');
  const emailIneligible = findEmail('application_received_ineligible');
  const emailTestLink = findEmail('test_link');
  const emailOutcome = findEmail('acceptance', 'test_failed', 'rejection');
  const emailAcceptance = findEmail('acceptance');
  const spotConfirmedEv = findEv('acceptance_confirmed');
  const emailWelcome = findEmail('welcome');

  const st = app.status;
  const passedStatuses = (list) => list.includes(st);
  const nk = app.next_action?.key;
  const stages = [];

  // 1. Application Submitted — always done
  stages.push({ label: 'Application Submitted', state: 'done', date: fmtDate(submittedEv?.created_at || app.submitted_at) });

  // 2. Eligibility Check
  if (!app.eligible) {
    stages.push({ label: 'Eligibility Check — Not Met', state: 'done', date: fmtDate(submittedEv?.created_at || app.submitted_at), variant: 'fail' });
    stages.push({ label: 'Ineligibility Email Sent', state: emailIneligible ? 'done' : 'upcoming', date: fmtDate(emailIneligible?.sent_at) });
    return stages;
  }
  stages.push({ label: 'Eligibility Check — Passed', state: 'done', date: fmtDate(submittedEv?.created_at || app.submitted_at) });

  // 3. Review & Shortlist
  const reviewDone = app.shortlisted !== null || passedStatuses(['screening', 'shortlisted', 'test_sent', 'test_invited', 'test_completed', 'accepted', 'selected_tba', 'rejected', 'enrolled']);
  const shortlistDate = fmtDate(shortlistedEv?.created_at || declinedEv?.created_at);
  const reviewState = reviewDone ? 'done' : (nk === 'review' || nk === 'close_review' ? 'current' : 'upcoming');

  if (app.shortlisted === false && !passedStatuses(['screening', 'shortlisted', 'test_sent', 'test_invited', 'test_completed', 'accepted', 'selected_tba', 'enrolled'])) {
    // Declined path
    stages.push({ label: 'Declined', state: reviewDone ? 'done' : reviewState, date: shortlistDate, variant: 'fail' });
    const emailDeclineState = emailNotShortlist ? 'done' : (nk === 'close_review' ? 'current' : 'upcoming');
    stages.push({ label: 'Decline Email Sent', state: emailDeclineState, date: fmtDate(emailNotShortlist?.sent_at) });
    return stages;
  }

  stages.push({ label: 'Shortlisted', state: reviewState, date: shortlistDate });

  // 4. Shortlist Email Sent
  const screeningPassed = passedStatuses(['screening', 'shortlisted', 'test_sent', 'test_invited', 'test_completed', 'accepted', 'selected_tba', 'rejected', 'enrolled']);
  const shortlistEmailState = emailShortlist ? 'done' : (nk === 'close_review' ? 'current' : (screeningPassed ? 'done' : 'upcoming'));
  stages.push({ label: 'Shortlist Email Sent', state: shortlistEmailState, date: fmtDate(emailShortlist?.sent_at || toScreeningEv?.created_at) });

  // 5. Attendance Confirmed
  const attendanceEv = findEv('attendance_confirmed');
  const attendanceRsvp = app.attendance_rsvp;
  const attendanceState = attendanceEv || attendanceRsvp === 'confirmed'
    ? 'done'
    : (shortlistEmailState === 'done' && !passedStatuses(['test_sent', 'test_invited', 'test_completed', 'accepted', 'selected_tba', 'rejected', 'enrolled']) ? 'current' : 'upcoming');
  if (app.shortlisted === true || passedStatuses(['shortlisted', 'test_sent', 'test_invited', 'test_completed', 'accepted', 'selected_tba', 'rejected', 'enrolled'])) {
    stages.push({ label: 'Attendance Confirmed', state: attendanceState, date: fmtDate(attendanceEv?.created_at) });
  }

  // 6. Aptitude Test Sent
  const testSentPassed = passedStatuses(['test_sent', 'test_invited', 'test_completed', 'accepted', 'selected_tba', 'rejected', 'enrolled']);
  const testSentState = (toTestSentEv || emailTestLink) ? 'done' : (nk === 'send_test' ? 'current' : (testSentPassed ? 'done' : 'upcoming'));
  stages.push({ label: 'Academic Assessment Sent', state: testSentState, date: fmtDate(toTestSentEv?.created_at || emailTestLink?.sent_at) });

  // 6. Test Completed
  const testDonePassed = passedStatuses(['test_completed', 'accepted', 'selected_tba', 'rejected', 'enrolled']);
  const testDoneState = toTestDoneEv ? 'done' : (nk === 'awaiting_test' ? 'current' : (testDonePassed ? 'done' : 'upcoming'));
  stages.push({ label: 'Academic Assessment Completed', state: testDoneState, date: fmtDate(toTestDoneEv?.created_at) });

  // 7. Outcome Decision
  const decisionPassed = passedStatuses(['accepted', 'selected_tba', 'rejected', 'enrolled']);
  const decisionLabel = st === 'accepted' ? 'Accepted' : (st === 'rejected' ? 'Rejected' : 'Outcome Decision');
  const decisionVariant = st === 'rejected' ? 'fail' : undefined;
  const decisionState = decisionPassed ? 'done' : (nk === 'decide' ? 'current' : 'upcoming');
  stages.push({ label: decisionLabel, state: decisionState, date: fmtDate(toAcceptedEv?.created_at || toRejectedEv?.created_at), variant: decisionVariant });

  if (st === 'rejected') {
    const emailRejState = emailOutcome ? 'done' : 'upcoming';
    stages.push({ label: 'Outcome Email Sent', state: emailRejState, date: fmtDate(emailOutcome?.sent_at) });
    return stages;
  }

  // 8. Offer Sent (acceptance email)
  const offerSentState = emailAcceptance ? 'done' : (nk === 'send_outcome' ? 'current' : 'upcoming');
  stages.push({ label: 'Offer Sent', state: offerSentState, date: fmtDate(emailAcceptance?.sent_at) });

  // 9. Spot Confirmed
  const spotConfirmedState = spotConfirmedEv || app.acceptance_confirmed
    ? 'done'
    : (offerSentState === 'done' ? 'current' : 'upcoming');
  stages.push({ label: 'Spot Confirmed', state: spotConfirmedState, date: fmtDate(spotConfirmedEv?.created_at) });

  // 9b. Selected TBA (holding cohort)
  if (st === 'selected_tba' || toEnrolledEv) {
    stages.push({
      label: 'Selected — Cohort Dates TBA',
      state: st === 'selected_tba' ? 'current' : (toEnrolledEv ? 'done' : 'upcoming'),
      date: null,
    });
  }

  // 10. Enrolled
  const enrolledState = toEnrolledEv ? 'done' : (nk === 'done' && st === 'enrolled' ? 'done' : 'upcoming');
  stages.push({ label: 'Enrolled', state: enrolledState, date: fmtDate(toEnrolledEv?.created_at || emailWelcome?.sent_at) });

  return stages;
}

export default function ApplicationReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const { returnIntakeId: stateIntakeId, applicationIds: stateIds, currentIndex: stateIndex } = location.state || {};
  const queueFromStorage = (() => {
    try { return JSON.parse(sessionStorage.getItem('reviewQueue') || 'null'); } catch { return null; }
  })();
  const applicationIds = (stateIds && stateIds.length > 0) ? stateIds : (queueFromStorage?.applicationIds || []);
  const returnIntakeId = stateIntakeId ?? queueFromStorage?.returnIntakeId;
  const currentIndex = (stateIndex != null && stateIndex >= 0) ? stateIndex : applicationIds.indexOf(id);

  const goBack = () => {
    navigate('/admin/applications', { state: { intakeId: returnIntakeId } });
  };

  const goToIndex = (idx) => {
    navigate(`/admin/applications/${applicationIds[idx]}`, {
      state: { returnIntakeId, applicationIds, currentIndex: idx },
    });
  };

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState([]);
  const [intakePeriods, setIntakePeriods] = useState([]);
  const [flagReason, setFlagReason] = useState('');
  const [showFlagInput, setShowFlagInput] = useState(false);
  const [editingNote, setEditingNote] = useState(null); // {created_at, text}
  const [blacklistReason, setBlacklistReason] = useState('');
  const [showBlacklistInput, setShowBlacklistInput] = useState(false);

  const formatFlagDate = (isoStr) => {
    const d = new Date(isoStr);
    return `${String(d.getDate()).padStart(2, '0')}/${d.getMonth() + 1}`;
  };

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  const [activeEduTab, setActiveEduTab] = useState('o_level');

  // Intake assignment
  const [selectedIntakeId, setSelectedIntakeId] = useState('');
  const [savingIntake, setSavingIntake] = useState(false);

  // Checklist & notes state
  const [checklist, setChecklist] = useState({});
  const [notes, setNotes] = useState('');
  const [savingChecklist, setSavingChecklist] = useState(false);

  // Test dialog state
  const [testData, setTestData] = useState(null);
  const [viewTestDialog, setViewTestDialog] = useState(false);

  // Activity log
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Email history + manual send
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null); // template_key being sent

  // Start Review drawer
  const [reviewDrawer, setReviewDrawer] = useState(false);
  const [reviewDocIndex, setReviewDocIndex] = useState(0);

  // Test activity
  const [testActivity, setTestActivity] = useState(null);
  const [testActivityLoading, setTestActivityLoading] = useState(false);

  // Internal notes
  const [internalNoteText, setInternalNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Edit personal info
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Override status modal
  const [overrideModal, setOverrideModal] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);

  // Email compose modal
  const [emailComposeModal, setEmailComposeModal] = useState(null); // { key, label }
  const [emailComposeValues, setEmailComposeValues] = useState({});

  // Test review (disqualify / approve)
  const [testReviewing, setTestReviewing] = useState(false);

  // Enroll dialog state
  const [enrollDialog, setEnrollDialog] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState('');
  const [enrolling, setEnrolling] = useState(false);

  // Checklist config (loaded from API, falls back to defaults)
  const [checklistSections, setChecklistSections] = useState(DEFAULT_CHECKLIST_SECTIONS);

  // Scoring config (loaded from API, falls back to defaults)
  const [scoringCfg, setScoringCfg] = useState({
    gpa_min_4: 2.8, gpa_min_5: 3.3,
    gpa_weight: 60, a_level_weight: 40,
  });

  // Document management
  const [docTags, setDocTags] = useState([]);
  const [addDocDialog, setAddDocDialog] = useState(false);
  const [addDocForm, setAddDocForm] = useState({ tag: '', tagInput: '', filename: '', file_data: '', file_type: '', note: '' });
  const [addDocLoading, setAddDocLoading] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Edit document tag/note
  const [editDocDialog, setEditDocDialog] = useState(false);
  const [editDocTarget, setEditDocTarget] = useState(null); // { id, tag, note }
  const [editDocForm, setEditDocForm] = useState({ tag: '', tagInput: '', note: '' });
  const [editDocLoading, setEditDocLoading] = useState(false);
  const [editTagSuggestions, setEditTagSuggestions] = useState([]);
  const [showEditTagSuggestions, setShowEditTagSuggestions] = useState(false);

  const token = () => localStorage.getItem('token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  const fetchEmailLogs = useCallback(async (email) => {
    setEmailLogsLoading(true);
    try {
      let url = `${API}/email-logs?entity_id=${id}&limit=50`;
      if (email) url += `&to_email=${encodeURIComponent(email)}`;
      const res = await axios.get(url, { headers: authHeader() });
      setEmailLogs(res.data);
    } catch { /* silently fail */ } finally {
      setEmailLogsLoading(false);
    }
  }, [id]);

  const fetchApp = useCallback(async () => {
    try {
      const [appRes, cohortsRes, intakesRes] = await Promise.all([
        axios.get(`${API}/applications/${id}`, { headers: authHeader() }),
        axios.get(`${API}/cohorts`, { headers: authHeader() }),
        axios.get(`${API}/intake-periods`, { headers: authHeader() }),
      ]);
      setApp(appRes.data);
      setCohorts(cohortsRes.data);
      setIntakePeriods(intakesRes.data);
      setChecklist(appRes.data.review_checklist || {});
      setNotes(appRes.data.admin_notes || '');
      setSelectedIntakeId(appRes.data.intake_id || '');
      fetchEmailLogs(appRes.data.email);
    } catch {
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id, fetchEmailLogs]);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await axios.get(`${API}/applications/${id}/events`, { headers: authHeader() });
      setEvents(res.data);
    } catch { /* silently fail */ } finally {
      setEventsLoading(false);
    }
  }, [id]);

  const fetchTestActivity = useCallback(async () => {
    setTestActivityLoading(true);
    try {
      const res = await axios.get(`${API}/applications/${id}/test-activity`, { headers: authHeader() });
      setTestActivity(res.data);
    } catch { /* no test yet */ } finally {
      setTestActivityLoading(false);
    }
  }, [id]);

  const handleAddInternalNote = async () => {
    if (!internalNoteText.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/applications/${id}/notes`, { text: internalNoteText.trim() }, { headers: authHeader() });
      setInternalNoteText('');
      fetchApp();
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); } finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/applications/${id}/notes/${noteId}`, { headers: authHeader() });
      fetchApp();
      toast.success('Note deleted');
    } catch { toast.error('Failed to delete note'); }
  };

  const handleOverrideStatus = async () => {
    if (!overrideStatus || !overrideReason.trim()) return;
    setOverriding(true);
    try {
      await axios.post(`${API}/applications/${id}/override-status`,
        { status: overrideStatus, reason: overrideReason.trim() },
        { headers: authHeader() }
      );
      toast.success('Status overridden');
      setOverrideModal(false);
      setOverrideStatus('');
      setOverrideReason('');
      fetchApp();
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to override status');
    } finally { setOverriding(false); }
  };

  const handleEditOpen = () => {
    setEditForm({
      full_name: app.full_name || '',
      email: app.email || '',
      phone: app.phone || '',
      date_of_birth: app.date_of_birth || '',
      nationality: app.nationality || '',
      address: app.address || '',
      llb_status: app.llb_status || 'completed',
      llb_gpa: app.llb_gpa ?? '',
      llb_gpa_scale: app.llb_gpa_scale ?? 5,
      referee1_name: app.referee1_name || '',
      referee1_title: app.referee1_title || '',
      referee1_institution: app.referee1_institution || '',
      referee1_email: app.referee1_email || '',
      referee1_telephone: app.referee1_telephone || '',
      referee2_name: app.referee2_name || '',
      referee2_title: app.referee2_title || '',
      referee2_institution: app.referee2_institution || '',
      referee2_email: app.referee2_email || '',
      referee2_telephone: app.referee2_telephone || '',
    });
    setEditMode(true);
  };

  const handleEditSave = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...editForm };
      if (payload.llb_gpa !== '') payload.llb_gpa = parseFloat(payload.llb_gpa);
      if (payload.llb_gpa_scale !== '') payload.llb_gpa_scale = parseInt(payload.llb_gpa_scale);
      const res = await axios.patch(`${API}/applications/${id}`, payload, { headers: authHeader() });
      setApp(res.data);
      if (res.data.recalculated) {
        const r = res.data.recalculated;
        toast.info(`Score recalculated: ${r.score?.toFixed(1)} | Eligible: ${r.eligible ? 'Yes' : 'No'}`);
      } else {
        toast.success('Application updated');
      }
      setEditMode(false);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save changes');
    } finally { setSavingEdit(false); }
  };

  const handleStageClick = ({ stageLabel, targetStatus }) => {
    setOverrideStatus(targetStatus);
    setOverrideReason('');
    setOverrideModal(true);
  };

  const handleTestReview = async (action) => {
    setTestReviewing(true);
    try {
      await axios.post(`${API}/applications/${id}/test-review`, { action }, { headers: authHeader() });
      toast.success(action === 'disqualify' ? 'Test disqualified' : 'Test approved');
      fetchApp();
      fetchTestActivity();
    } catch { toast.error('Failed to update test review'); } finally { setTestReviewing(false); }
  };

  // Per-template editable placeholders. Each entry: { key, label, defaultFn, hint? }
  const TEMPLATE_PLACEHOLDERS = {
    shortlisted: [
      { key: 'test_date',     label: 'Assessment Date',     defaultFn: (a, i) => i?.aptitude_test_date || '' },
      { key: 'test_time',     label: 'Assessment Time',     defaultFn: (a, i) => i?.aptitude_test_time || '' },
      { key: 'test_mode',     label: 'Mode',                defaultFn: () => 'Online' },
      { key: 'test_duration', label: 'Duration',            defaultFn: (a, i) => i?.aptitude_test_duration_hours ? `${Math.round(i.aptitude_test_duration_hours)} minutes` : '' },
    ],
    test_link: [
      { key: 'test_date',       label: 'Test Date',                defaultFn: (a, i) => i?.aptitude_test_date || '' },
      { key: 'test_time',       label: 'Test Time',                defaultFn: (a, i) => i?.aptitude_test_time || '' },
      { key: 'test_mode',       label: 'Mode (Online / In-Person)', defaultFn: () => 'Online' },
      { key: 'test_duration',   label: 'Duration',                 defaultFn: (a, i) => i?.aptitude_test_duration_hours ? `${Math.round(i.aptitude_test_duration_hours)} minutes` : '2 hours' },
      { key: 'test_login_time', label: 'Log-in Time',              defaultFn: () => '15 minutes before the scheduled time' },
    ],
    acceptance: [
      { key: 'cohort_name',         label: 'Cohort Name',          defaultFn: (a) => a?.cohort_name || '' },
      { key: 'cohort_start_date',   label: 'Programme Start Date', defaultFn: (a) => a?.cohort_start_date || '' },
      { key: 'cohort_start_time',   label: 'Start Time',           defaultFn: (a) => a?.cohort_start_time || '' },
      { key: 'acceptance_deadline', label: 'Acceptance Deadline',  defaultFn: (a) => a?.acceptance_deadline || '' },
      { key: 'cohort_number',       label: 'Cohort Number',        defaultFn: () => '' },
    ],
    application_received_ineligible: [
      { key: 'unmet_criteria', label: 'Reason(s) Not Met', defaultFn: (a) => a?.ineligibility_reason || '' },
    ],
    not_shortlisted: [
      { key: 'unmet_criteria', label: 'Reason(s) Not Met', defaultFn: (a) => a?.ineligibility_reason || '' },
    ],
  };

  const openEmailCompose = (key, label) => {
    const fields = TEMPLATE_PLACEHOLDERS[key] || [];
    const intake = intakePeriods.find(p => p.id === app?.intake_id) || null;
    const defaults = {};
    fields.forEach(f => { defaults[f.key] = f.defaultFn(app, intake, cohorts); });
    setEmailComposeValues(defaults);
    setEmailComposeModal({ key, label });
  };

  const handleSendEmailConfirm = async () => {
    if (!emailComposeModal) return;
    const { key } = emailComposeModal;
    setSendingEmail(key);
    setEmailComposeModal(null);
    try {
      await axios.post(
        `${API}/applications/${id}/send-email`,
        { template_key: key, placeholder_overrides: emailComposeValues },
        { headers: authHeader() }
      );
      toast.success(`Email queued: ${key}`);
      setTimeout(() => fetchEmailLogs(app?.email), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleSendEmail = (key, label) => openEmailCompose(key, label);

  useEffect(() => {
    if (activeTab === 'test_activity' && !testActivity && !testActivityLoading) {
      fetchTestActivity();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchApp(); // also triggers fetchEmailLogs(email) after app loads
    fetchEvents();
    axios.get(`${API}/document-tags`, { headers: authHeader() })
      .then(r => setDocTags(r.data.tags || []))
      .catch(() => {});
    axios.get(`${API}/review-checklist-config`, { headers: authHeader() })
      .then(r => { if (r.data.sections?.length) setChecklistSections(r.data.sections); })
      .catch(() => {});
    axios.get(`${API}/scoring-config`, { headers: authHeader() })
      .then(r => { if (r.data) setScoringCfg(c => ({ ...c, ...r.data })); })
      .catch(() => {});
  }, [fetchApp, fetchEvents]);

  const handleAddDocFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAddDocForm(f => ({ ...f, file_data: ev.target.result, filename: file.name, file_type: file.type }));
    };
    reader.readAsDataURL(file);
  };

  const handleTagInput = (val) => {
    setAddDocForm(f => ({ ...f, tagInput: val, tag: val }));
    if (val.trim()) {
      const filtered = docTags.filter(t => t.toLowerCase().includes(val.toLowerCase()));
      setTagSuggestions(filtered);
      setShowTagSuggestions(true);
    } else {
      setShowTagSuggestions(false);
    }
  };

  const handleAddDocument = async () => {
    if (!addDocForm.tag.trim()) { toast.error('Please enter a tag'); return; }
    if (!addDocForm.file_data) { toast.error('Please select a file'); return; }
    setAddDocLoading(true);
    try {
      await axios.post(`${API}/applications/${id}/documents`, {
        tag: addDocForm.tag.trim(),
        filename: addDocForm.filename,
        file_data: addDocForm.file_data,
        file_type: addDocForm.file_type,
        note: addDocForm.note.trim() || null,
      }, { headers: authHeader() });
      toast.success('Document added');
      setAddDocDialog(false);
      setAddDocForm({ tag: '', tagInput: '', filename: '', file_data: '', file_type: '', note: '' });
      const [tagsRes] = await Promise.all([
        axios.get(`${API}/document-tags`, { headers: authHeader() }),
        fetchApp(),
      ]);
      setDocTags(tagsRes.data.tags || []);
    } catch {
      toast.error('Failed to add document');
    } finally {
      setAddDocLoading(false);
    }
  };

  const handleDeleteAdminDocument = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await axios.delete(`${API}/applications/${id}/documents/${docId}`, { headers: authHeader() });
      toast.success('Document removed');
      fetchApp();
    } catch {
      toast.error('Failed to remove document');
    }
  };

  const openEditDoc = (doc) => {
    setEditDocTarget(doc);
    setEditDocForm({ tag: doc.tag, tagInput: doc.tag, note: doc.note || '' });
    setEditDocDialog(true);
  };

  const handleEditTagInput = (val) => {
    setEditDocForm(f => ({ ...f, tagInput: val, tag: val }));
    const filtered = val.trim() ? docTags.filter(t => t.toLowerCase().includes(val.toLowerCase())) : docTags;
    setEditTagSuggestions(filtered);
    setShowEditTagSuggestions(true);
  };

  const handleUpdateDocument = async () => {
    if (!editDocForm.tag.trim()) { toast.error('Tag is required'); return; }
    setEditDocLoading(true);
    try {
      await axios.patch(
        `${API}/applications/${id}/documents/${editDocTarget.id}`,
        { tag: editDocForm.tag.trim(), note: editDocForm.note.trim() || null },
        { headers: authHeader() }
      );
      toast.success('Document updated');
      setEditDocDialog(false);
      const [tagsRes] = await Promise.all([
        axios.get(`${API}/document-tags`, { headers: authHeader() }),
        fetchApp(),
      ]);
      setDocTags(tagsRes.data.tags || []);
    } catch {
      toast.error('Failed to update document');
    } finally {
      setEditDocLoading(false);
    }
  };

  const parseQuals = (jsonStr) => {
    try { return JSON.parse(jsonStr); } catch { return {}; }
  };

  const handleChecklistToggle = (key) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveChecklist = async () => {
    setSavingChecklist(true);
    try {
      await axios.patch(`${API}/applications/${id}`, { review_checklist: checklist, admin_notes: notes }, { headers: authHeader() });
      toast.success('Review saved');
    } catch {
      toast.error('Failed to save review');
    } finally {
      setSavingChecklist(false);
    }
  };

  const handleShortlist = async (value) => {
    try {
      await axios.patch(`${API}/applications/${id}/shortlist?shortlisted=${value}`, {}, { headers: authHeader() });
      setApp(prev => ({ ...prev, shortlisted: value }));
      toast.success(value ? 'Application shortlisted' : 'Application declined');
    } catch {
      toast.error('Failed to update shortlist');
    }
  };

  const handleFlag = async () => {
    if (!flagReason.trim()) return;
    try {
      const res = await axios.patch(`${API}/applications/${id}/flag`, { reason: flagReason.trim() }, { headers: authHeader() });
      setApp(res.data);
      setShowFlagInput(false);
      setFlagReason('');
      toast.success('Application flagged');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to flag application');
    }
  };

  const handleUnflag = async () => {
    try {
      const res = await axios.patch(`${API}/applications/${id}/unflag`, {}, { headers: authHeader() });
      setApp(res.data);
      toast.success('Flag removed');
    } catch {
      toast.error('Failed to remove flag');
    }
  };

  const handleBlacklist = async () => {
    if (!blacklistReason.trim()) return;
    try {
      const res = await axios.patch(`${API}/applications/${id}/blacklist`, { reason: blacklistReason.trim() }, { headers: authHeader() });
      setApp(res.data);
      setShowBlacklistInput(false);
      setBlacklistReason('');
      toast.success('Application blacklisted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to blacklist application');
    }
  };

  const handleUnblacklist = async () => {
    try {
      const res = await axios.patch(`${API}/applications/${id}/unblacklist`, {}, { headers: authHeader() });
      setApp(res.data);
      toast.success('Removed from blacklist');
    } catch {
      toast.error('Failed to remove from blacklist');
    }
  };

  const handleDeleteFlagNote = async (createdAt) => {
    try {
      const res = await axios.delete(`${API}/applications/${id}/flag-notes`, {
        params: { created_at: createdAt },
        headers: authHeader(),
      });
      setApp(res.data);
      toast.success('Note removed');
    } catch {
      toast.error('Failed to remove note');
    }
  };

  const handleEditFlagNote = async () => {
    if (!editingNote || !editingNote.text.trim()) return;
    try {
      const res = await axios.patch(`${API}/applications/${id}/flag-notes`, {
        created_at: editingNote.created_at,
        text: editingNote.text.trim(),
      }, { headers: authHeader() });
      setApp(res.data);
      setEditingNote(null);
      toast.success('Note updated');
    } catch {
      toast.error('Failed to update note');
    }
  };

  const handleSendTest = async () => {
    try {
      await axios.patch(`${API}/applications/${id}/status?status=test_invited`, {}, { headers: authHeader() });
      setApp(prev => ({ ...prev, status: 'test_invited' }));
      toast.success('Assessment link sent to applicant');
    } catch {
      toast.error('Failed to send assessment link');
    }
  };

  const handleAcceptApplicant = async () => {
    try {
      await axios.patch(`${API}/applications/${id}/status?status=accepted`, {}, { headers: authHeader() });
      setApp(prev => ({ ...prev, status: 'accepted' }));
      toast.success('Applicant accepted');
    } catch {
      toast.error('Failed to accept applicant');
    }
  };

  const handleHoldTBA = async () => {
    if (!window.confirm('Mark as Selected — Dates TBA? The applicant will receive a "selected, cohort dates to be confirmed" email.')) return;
    try {
      await axios.patch(`${API}/applications/${id}/status?status=selected_tba`, {}, { headers: authHeader() });
      setApp(prev => ({ ...prev, status: 'selected_tba' }));
      toast.success('Applicant placed in holding — TBA email sent');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleRejectApplicant = async () => {
    if (!window.confirm('Reject this applicant? A rejection email will be sent.')) return;
    try {
      await axios.patch(`${API}/applications/${id}/status?status=rejected`, {}, { headers: authHeader() });
      setApp(prev => ({ ...prev, status: 'rejected' }));
      toast.success('Applicant rejected');
    } catch {
      toast.error('Failed to reject applicant');
    }
  };

  const handleResendTest = async () => {
    if (!window.confirm('Resend the Academic Assessment link to this applicant?')) return;
    try {
      await axios.post(`${API}/applications/${id}/resend-test`, {}, { headers: authHeader() });
      toast.success('Assessment link resent successfully');
    } catch {
      toast.error('Failed to resend assessment link');
    }
  };

  const handleViewTest = async () => {
    try {
      const res = await axios.get(`${API}/aptitude-tests/application/${id}`, { headers: authHeader() });
      setTestData(res.data);
      setViewTestDialog(true);
    } catch {
      toast.error('No test found for this application');
    }
  };

  const handleEnroll = async () => {
    if (!selectedCohort) { toast.error('Select a cohort first'); return; }
    setEnrolling(true);
    try {
      await axios.post(`${API}/students?application_id=${id}&cohort_id=${selectedCohort}`, {}, { headers: authHeader() });
      toast.success('Student enrolled successfully');
      setEnrollDialog(false);
      fetchApp();
    } catch {
      toast.error('Failed to enroll student');
    } finally {
      setEnrolling(false);
    }
  };

  const handleManualConfirmAndEnroll = async () => {
    if (!window.confirm(`Manually confirm ${app.full_name}'s spot and enroll them? This records admin confirmation on their behalf.`)) return;
    try {
      await axios.post(`${API}/applications/${id}/manual-confirm`, {}, { headers: authHeader() });
      await fetchApp();
      setSelectedCohort(cohorts[0]?.id || '');
      setEnrollDialog(true);
    } catch {
      toast.error('Failed to confirm application');
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm(`Mark ${app.full_name} as Withdrawn? This will remove them from their cohort.`)) return;
    try {
      await axios.patch(`${API}/applications/${id}/withdraw`, {}, { headers: authHeader() });
      toast.success(`${app.full_name} marked as withdrawn`);
      fetchApp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to withdraw applicant');
    }
  };

  const handleExportPDF = async () => {
    toast.info('Generating PDF...');
    try {
      await generateApplicationPDFWithLibrary(app);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const handleAssignIntake = async () => {
    setSavingIntake(true);
    try {
      await axios.patch(`${API}/applications/${id}`, { intake_id: selectedIntakeId || null }, { headers: authHeader() });
      setApp(prev => ({ ...prev, intake_id: selectedIntakeId || null }));
      toast.success(selectedIntakeId ? 'Intake assigned' : 'Intake cleared');
    } catch {
      toast.error('Failed to assign intake');
    } finally {
      setSavingIntake(false);
    }
  };

  const downloadFile = async (dataOrKey, name, type) => {
    const ok = await downloadFileWithCorrectType(dataOrKey, name, type);
    if (!ok) toast.error('Failed to download file');
  };

  const getScoreBreakdown = () => {
    if (!app || app.score == null) return null;
    const GRADE_PTS = { A: 6, B: 5, C: 4, D: 3, E: 2, S: 1, F: 0 };
    const extra = parseQuals(app.additional_qualifications);

    // GPA scale bounds: 4-point scale min=2.8, 5-point scale min=3.3
    const scale = app.llb_gpa_scale || 5;
    const GPA_MAX = { 4: 4.0, 5: 5.0 };
    const gpaMin = scoringCfg[`gpa_min_${scale}`] ?? (scale === 4 ? 2.8 : 3.3);
    const gpaMax = GPA_MAX[scale] ?? 5.0;
    const gpaW   = scoringCfg.gpa_weight ?? 60;
    const aLvlW  = scoringCfg.a_level_weight ?? 40;

    const gpa = app.llb_gpa || 0;
    // Linear interpolation: (GPA - min) / (max - min) × weight
    const gpaRange = gpaMax - gpaMin;
    const gpaScore = (gpa >= gpaMin && gpaRange > 0) ? Math.min(((gpa - gpaMin) / gpaRange) * gpaW, gpaW) : 0;

    // Top-3 subjects by points
    const subjects = (extra?.a_levels?.subjects || []).filter(s => s.subject && s.grade);
    const subjectDetails = subjects.map(s => ({
      subject: s.subject, grade: s.grade?.trim().toUpperCase(),
      points: GRADE_PTS[s.grade?.trim().toUpperCase()] ?? 0,
    })).sort((a, b) => b.points - a.points).slice(0, 3);
    const gradeSum = subjectDetails.reduce((sum, s) => sum + s.points, 0);
    const aLevelScore = (gradeSum / 18) * aLvlW;

    return { gpa, gpaMin, gpaMax, gpaW, aLvlW, gpaScore, subjectDetails, gradeSum, aLevelScore };
  };

  const checklistItems = checklistSections.flatMap(s => s.items);
  const checkedCount = checklistItems.filter(i => checklist[i.key]).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading application…</div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-lg mb-4 text-muted-foreground">Application not found</p>
          <Button onClick={goBack}>Back to Applications</Button>
        </div>
      </div>
    );
  }

  const extra = parseQuals(app.additional_qualifications);
  const personal = extra?.personal_details || {};
  const oLevels = extra?.o_levels || {};
  const aLevels = extra?.a_levels || {};
  const llb = extra?.llb || {};
  const bd = getScoreBreakdown();

  // Prefer first_name + last_name from personal details; fall back to full_name
  const displayName = [personal.first_name, personal.last_name].filter(Boolean).join(' ') || app.full_name || '';

  const initials = (displayName || 'A')
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const applicantDocs = [
    { data: app.nida_passport_copy,    name: app.nida_passport_copy_filename    || 'NIDA_Passport.pdf',      type: app.nida_passport_copy_type,    tag: 'NIDA / Passport / ID Card' },
    { data: app.additional_certificate, name: app.additional_certificate_filename || 'O_Level_Certificate.pdf', type: app.additional_certificate_type, tag: 'O-Level Certificate' },
    { data: app.a_level_certificate,   name: app.a_level_certificate_filename   || 'A_Level_Certificate.pdf', type: app.a_level_certificate_type,   tag: 'A-Level Certificate' },
    { data: app.llb_certificate,       name: app.llb_certificate_filename       || 'LLB_Certificate.pdf',     type: app.llb_certificate_type,       tag: 'LLB Certificate' },
  ].filter(d => d.data);

  const adminDocs = app.additional_documents || [];
  const docCount = applicantDocs.length + adminDocs.length;

  const integrityLevel = app.test_disqualified ? 'disqualified' : (app.integrity_level || null);
  const TABS = [
    { id: 'overview', label: 'Overview', icon: <User className="w-4 h-4" /> },
    { id: 'documents', label: `Documents${docCount > 0 ? ` (${docCount})` : ''}`, icon: <FileText className="w-4 h-4" /> },
    { id: 'review', label: 'Review & Notes', icon: <CheckSquare className="w-4 h-4" /> },
    { id: 'test_activity', label: `Test Activity${integrityLevel && integrityLevel !== 'clean' ? ' ⚠' : ''}`, icon: <Activity className="w-4 h-4" /> },
    { id: 'emails', label: `Emails${emailLogs.length > 0 ? ` (${emailLogs.length})` : ''}`, icon: <Mail className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Hero Header ───────────────────────────────────────────────────────── */}
      <div className="bg-white border-b">
        {/* Top bar: back + prev/next + export */}
        <div className="px-6 pt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="-ml-2 text-muted-foreground hover:text-primary" onClick={goBack}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {applicationIds.length > 0 && (
              <div className="flex items-center gap-1 ml-1">
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentIndex <= 0} onClick={() => goToIndex(currentIndex - 1)} title="Previous applicant">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums px-1">{currentIndex + 1} / {applicationIds.length}</span>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentIndex >= applicationIds.length - 1} onClick={() => goToIndex(currentIndex + 1)} title="Next applicant">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileDown className="w-4 h-4 mr-1" /> Export PDF
          </Button>
        </div>

        {/* Hero body */}
        <div className="px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center text-white text-2xl font-bold font-playfair shrink-0 shadow-md">
            {initials}
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0">
            <h1 className="font-playfair font-bold text-2xl text-primary leading-tight truncate">{displayName}</h1>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{app.email}</span>
              {app.phone && <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 text-center text-xs">☎</span>{app.phone}</span>}
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Submitted {fmtDate(app.submitted_at)}</span>
            </div>

            {/* Badge row */}
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                {STATUS_LABELS[app.status] || app.status}
              </span>
              {app.eligible
                ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Eligible</span>
                : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200"><XCircle className="w-3 h-3 mr-1" />Ineligible</span>
              }
              {app.score != null && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${scoreBg(app.score)}`}>
                  <BarChart2 className="w-3 h-3 mr-1" />App {app.score.toFixed(1)}
                </span>
              )}
              {app.aptitude_score != null && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${scoreBg(app.aptitude_score)}`}>
                  <BookOpen className="w-3 h-3 mr-1" />Test {app.aptitude_score.toFixed(0)}%
                </span>
              )}
              {app.final_score != null && (
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${scoreBg(app.final_score)}`}>
                  Final {app.final_score.toFixed(1)}
                </span>
              )}
              {app.shortlisted === true && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                  <Star className="w-3 h-3 mr-1 fill-yellow-500" />Shortlisted
                </span>
              )}
              {app.shortlisted === true && app.attendance_rsvp === 'confirmed' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700 border border-teal-200">
                  ✓ Attendance Confirmed
                </span>
              )}
              {app.shortlisted === true && app.attendance_rsvp === 'pending' && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  ⏳ Awaiting RSVP
                </span>
              )}
              {app.shortlisted === false && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                  Declined
                </span>
              )}
              {app.intake_id && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  {intakePeriods.find(i => i.id === app.intake_id)?.name || 'Intake assigned'}
                </span>
              )}
              {app.status === 'enrolled' && app.cohort_name && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                  🎓 {app.cohort_name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Flag Banner */}
        {app.flagged && (
          <div className="px-6 pt-4 border-t">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <Flag className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-800 mb-1">Flagged for review</p>
                {(app.flag_notes && app.flag_notes.length > 0) ? (
                  <div className="space-y-1">
                    {[...app.flag_notes].reverse().map((note, i) => (
                      editingNote?.created_at === note.created_at ? (
                        <div key={i} className="flex items-center gap-1.5">
                          <Input
                            value={editingNote.text}
                            onChange={e => setEditingNote(prev => ({ ...prev, text: e.target.value }))}
                            className="h-6 text-xs flex-1 py-0"
                            onKeyDown={e => { if (e.key === 'Enter') handleEditFlagNote(); if (e.key === 'Escape') setEditingNote(null); }}
                            autoFocus
                          />
                          <button onClick={handleEditFlagNote} className="text-[10px] text-red-600 hover:text-red-800 font-medium flex-shrink-0">Save</button>
                          <button onClick={() => setEditingNote(null)} className="text-[10px] text-gray-400 hover:text-gray-600 flex-shrink-0">Cancel</button>
                        </div>
                      ) : (
                        <div key={i} className="flex items-start gap-1.5">
                          <p className="text-xs text-red-700 flex-1">
                            <span className="font-medium">{formatFlagDate(note.created_at)}</span> — {note.text}
                          </p>
                          <button onClick={() => setEditingNote({ created_at: note.created_at, text: note.text })} className="flex-shrink-0 text-red-300 hover:text-red-600"><Pencil className="w-2.5 h-2.5" /></button>
                          <button onClick={() => handleDeleteFlagNote(note.created_at)} className="text-[11px] text-red-300 hover:text-red-700 flex-shrink-0">×</button>
                        </div>
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-red-700">{app.flag_reason}</p>
                )}
              </div>
              {(!app.flag_notes || app.flag_notes.length === 0) && (
                <button onClick={handleUnflag} className="text-xs text-red-600 hover:text-red-800 underline flex-shrink-0">Remove flag</button>
              )}
            </div>
          </div>
        )}

        {/* Prior Blacklist Warning */}
        {app.prior_blacklist_flag && (
          <div className="px-6 pt-3 border-t">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-orange-50 border border-orange-300">
              <Ban className="w-4 h-4 text-orange-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-orange-800">Blacklisted Applicant</p>
                <p className="text-xs text-orange-700 mt-0.5">This applicant has been blacklisted on a previous application. Review their history carefully before proceeding.</p>
              </div>
            </div>
          </div>
        )}

        {/* Stage Path */}
        <div className="px-6 pb-4 border-t pt-4">
          <ApplicationStagePath app={app} events={events} emailLogs={emailLogs} onStageClick={handleStageClick} />
        </div>

        {/* Tab bar */}
        <div className="px-6 flex gap-1 border-t">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main Body ─────────────────────────────────────────────────────────── */}
      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-screen-xl mx-auto">

        {/* ── Left: Tab Content (2 cols) ──────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* ━━━━━━━━━━━ OVERVIEW TAB ━━━━━━━━━━━ */}
          {activeTab === 'overview' && (
            <>
              {/* Score Breakdown — first and most prominent */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart2 className="w-4 h-4 text-primary" /> Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {!app.eligible ? (
                    <Alert className="border-red-300 bg-red-50">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-700 ml-1">
                        {app.ineligibility_reason || 'Applicant does not meet eligibility criteria'}
                      </AlertDescription>
                    </Alert>
                  ) : bd ? (
                    <>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Academic Score (out of 100)</p>
                        <ScoreBar label="LLB GPA" sub={`${bd.gpaW}%`} value={bd.gpaScore} max={bd.gpaW} color="bg-blue-500"
                          detail={`GPA ${bd.gpa} (scale ${bd.gpaMin}–${bd.gpaMax}) → ${bd.gpaScore.toFixed(2)} pts`} />
                        <ScoreBar label="A-Level Grades" sub={`${bd.aLvlW}%`} value={bd.aLevelScore} max={bd.aLvlW} color="bg-green-500"
                          detail={bd.subjectDetails.length ? bd.subjectDetails.map(s => `${s.subject}: ${s.grade} (${s.points})`).join(' · ') : 'No grades entered'} />
                        <div className={`rounded-lg border-2 p-3 flex items-center justify-between ${scoreBg(app.score)}`}>
                          <span className="font-semibold text-sm">Application Score</span>
                          <span className={`text-2xl font-bold ${scoreColor(app.score)}`}>{app.score.toFixed(1)}<span className="text-sm font-normal text-muted-foreground"> / 100</span></span>
                        </div>
                      </div>

                      {app.aptitude_score != null && (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Academic Assessment</p>
                          <div className={`rounded-lg border-2 p-3 flex items-center justify-between ${scoreBg(app.aptitude_score)}`}>
                            <div>
                              <span className="font-semibold text-sm">MCQ Score</span>
                              {app.status === 'test_completed' && (
                                <button onClick={handleViewTest} className="ml-3 text-xs text-blue-600 hover:underline flex items-center gap-1 inline-flex">
                                  <Eye className="w-3 h-3" /> View responses
                                </button>
                              )}
                            </div>
                            <span className={`text-2xl font-bold ${scoreColor(app.aptitude_score)}`}>{app.aptitude_score.toFixed(1)}<span className="text-sm font-normal text-muted-foreground">%</span></span>
                          </div>
                        </div>
                      )}

                      {app.final_score != null && (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Final Combined Score</p>
                          <div className={`rounded-xl border-2 p-4 flex items-center justify-between ${scoreBg(app.final_score)}`}>
                            <div>
                              <span className="font-bold text-sm">Final Score</span>
                              <p className="text-xs text-muted-foreground">Academic + Aptitude (weighted)</p>
                            </div>
                            <span className={`text-3xl font-bold ${scoreColor(app.final_score)}`}>{app.final_score.toFixed(1)}<span className="text-sm font-normal text-muted-foreground"> / 100</span></span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No score calculated yet.</p>
                  )}
                </CardContent>
              </Card>

              {/* Personal Information */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 justify-between">
                    <span className="flex items-center gap-2"><User className="w-4 h-4 text-primary" /> Personal Information</span>
                    <Button size="sm" variant="outline" onClick={handleEditOpen} className="h-7 text-xs gap-1">
                      <Pencil className="w-3 h-3" /> Edit
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                    <FieldV label="Full Name" value={app.full_name} />
                    <FieldV label="Date of Birth" value={fmtDate(app.date_of_birth) || app.date_of_birth} />
                    <FieldV label="First Name" value={personal.first_name} />
                    <FieldV label="Middle Name" value={personal.middle_name} />
                    <FieldV label="Last Name" value={personal.last_name} />
                    <FieldV label="Age" value={extra?.age ? `${extra.age} years` : undefined} />
                    <FieldV label="Place of Birth" value={personal.birth_place} />
                    <FieldV label="Nationality" value={app.nationality} />
                    <FieldV label="NIDA / Passport No." value={personal.nida_passport} />
                    <FieldV label="ID Expiry" value={personal.id_expiry} />
                    <FieldV label="Email" value={app.email} />
                    <FieldV label="Mobile" value={app.phone} />
                    <FieldV label="WhatsApp" value={personal.whatsapp || 'Same as mobile'} />
                    <FieldV label="Address" value={app.address} span={2} />
                    <FieldV
                      label="Employment / Internship"
                      value={app.currently_employed === true
                        ? `Employed / Intern${app.employer_name ? ` — ${app.employer_name}` : ''}`
                        : app.currently_employed === false
                          ? 'Not employed / in internship'
                          : undefined}
                      span={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Academic Background — tabbed */}
              <Card>
                <CardHeader className="pb-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-primary" /> Academic Background
                  </CardTitle>
                  <div className="flex gap-1 mt-3 border-b -mx-6 px-6">
                    {[
                      { id: 'o_level', label: 'O-Level' },
                      { id: 'a_level', label: 'A-Level' },
                      { id: 'llb',     label: 'LLB' },
                    ].map(t => (
                      <button
                        key={t.id}
                        onClick={() => setActiveEduTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          activeEduTab === t.id
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-gray-700'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent className="pt-5">
                  {activeEduTab === 'o_level' && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                      <FieldV label="School" value={oLevels.school} />
                      <FieldV label="Year of Graduation" value={oLevels.year} />
                      <FieldV label="Division" value={oLevels.division} />
                    </div>
                  )}
                  {activeEduTab === 'a_level' && (
                    <div className="space-y-4 text-sm">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <FieldV label="School" value={aLevels.school} />
                        <FieldV label="Year of Graduation" value={aLevels.year} />
                        <FieldV label="Division" value={aLevels.division || app.a_level_division} />
                      </div>
                      {aLevels.subjects && aLevels.subjects.length > 0 && (
                        <table className="w-full text-sm border rounded-lg overflow-hidden mt-2">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subject</th>
                              <th className="text-center px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Grade</th>
                            </tr>
                          </thead>
                          <tbody>
                            {aLevels.subjects.filter(s => s.subject).map((s, i) => (
                              <tr key={i} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-2">{s.subject}</td>
                                <td className="text-center px-4 py-2 font-bold text-primary">{s.grade || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {aLevels.no_a_level_details && (
                        <p className="text-sm mt-2"><strong>Alternative qualification:</strong> {aLevels.no_a_level_details}</p>
                      )}
                    </div>
                  )}
                  {activeEduTab === 'llb' && (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                      <FieldV label="Status" value={app.llb_status} />
                      <FieldV label="CGPA" value={app.llb_gpa != null ? `${app.llb_gpa} / ${app.llb_gpa_scale || 5}.0` : null} />
                      <FieldV label="Class" value={llb.class} />
                      <FieldV label="Graduation Date" value={fmtDate(llb.graduation_date) || llb.graduation_date} />
                      <FieldV label="Institute" value={llb.institute} />
                      <FieldV label="Years Attended" value={llb.years_attended} />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Personal Statement */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Pencil className="w-4 h-4 text-primary" /> Personal Statement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 p-4 rounded-lg border text-sm whitespace-pre-line leading-relaxed">
                    {extra?.personal_statement || <span className="text-muted-foreground italic">No statement provided</span>}
                  </div>
                </CardContent>
              </Card>

              {/* Referees */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" /> Referees
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { label: 'Referee 1', name: app.referee1_name, title: app.referee1_title, inst: app.referee1_institution, email: app.referee1_email, tel: app.referee1_telephone },
                      { label: 'Referee 2', name: app.referee2_name, title: app.referee2_title, inst: app.referee2_institution, email: app.referee2_email, tel: app.referee2_telephone },
                    ].map(r => (
                      <div key={r.label} className="bg-gray-50 p-4 rounded-lg border space-y-3">
                        <p className="font-semibold text-sm text-primary">{r.label}</p>
                        <div className="space-y-2 text-sm">
                          <FieldV label="Name" value={r.name} />
                          <FieldV label="Title / Position" value={r.title} />
                          <FieldV label="Institution" value={r.inst} />
                          <FieldV label="Email" value={r.email} />
                          <FieldV label="Telephone" value={r.tel} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Declarations */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" /> Declarations & Signature
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <DeclarationRow checked={app.declaration_info_true} label="Information provided is true and accurate" />
                  <DeclarationRow checked={app.declaration_competitive} label="Acknowledges competitive nature of programme" />
                  <DeclarationRow checked={app.declaration_aptitude_tests} label="Willing to undertake Academic Assessment and assessments" />
                  <DeclarationRow checked={app.declaration_terms_agreed} label="Agreed to Terms and Conditions" />
                  <p className="text-muted-foreground pt-1">Declaration date: <strong>{app.declaration_date || 'N/A'}</strong></p>
                </CardContent>
              </Card>
            </>
          )}

          {/* ━━━━━━━━━━━ DOCUMENTS TAB ━━━━━━━━━━━ */}
          {activeTab === 'documents' && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" /> Application Documents
                  </CardTitle>
                  <Button size="sm" onClick={() => { setAddDocForm({ tag: '', tagInput: '', filename: '', file_data: '', file_type: '', note: '' }); setAddDocDialog(true); }}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Document
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {applicantDocs.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Submitted by Applicant</p>
                    {applicantDocs.map(doc => (
                      <div key={doc.tag} className="flex items-center justify-between border rounded-lg px-4 py-3 bg-blue-50 border-blue-100 hover:border-blue-200 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{doc.name}</p>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1 mt-0.5">
                              <Tag className="w-2.5 h-2.5" />{doc.tag}
                            </span>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800" onClick={() => downloadFile(doc.data, doc.name, doc.type)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {adminDocs.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Uploaded by Admin</p>
                    {adminDocs.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between border rounded-lg px-4 py-3 bg-amber-50 border-amber-100 hover:border-amber-200 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-amber-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{doc.filename}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <Tag className="w-2.5 h-2.5" />{doc.tag}
                              </span>
                              <span className="text-xs text-muted-foreground">{doc.uploaded_by?.split('@')[0] || 'Admin'}</span>
                              {doc.note && <span className="text-xs text-muted-foreground italic truncate max-w-[120px]">{doc.note}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" className="text-amber-600 hover:text-amber-800" onClick={() => downloadFile(doc.file_data, doc.filename, doc.file_type)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-gray-500 hover:text-gray-800" onClick={() => openEditDoc(doc)} title="Edit tag / note">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteAdminDocument(doc.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {docCount === 0 && (
                  <div className="text-center py-10 text-muted-foreground">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No documents uploaded yet.</p>
                    <p className="text-xs mt-1">Click "Add Document" to upload the first one.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ━━━━━━━━━━━ REVIEW & NOTES TAB ━━━━━━━━━━━ */}
          {activeTab === 'review' && (
            <>
              <div className="grid md:grid-cols-2 gap-5">
                {/* Review Checklist */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Review Checklist</CardTitle>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        checkedCount === checklistItems.length ? 'bg-green-100 text-green-700' :
                        checkedCount > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {checkedCount} / {checklistItems.length}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-2">
                      <div
                        className={`h-full rounded-full transition-all ${checkedCount === checklistItems.length ? 'bg-green-500' : 'bg-primary'}`}
                        style={{ width: `${(checkedCount / checklistItems.length) * 100}%` }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    {checklistItems.map(item => (
                      <button
                        key={item.key}
                        className="w-full flex items-start gap-2.5 text-left text-sm p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                        onClick={() => handleChecklistToggle(item.key)}
                      >
                        {checklist[item.key]
                          ? <CheckSquare className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                          : <Square className="w-4 h-4 text-gray-300 shrink-0 mt-0.5 group-hover:text-gray-500" />
                        }
                        <span className={checklist[item.key] ? 'text-green-700 line-through text-xs' : 'text-gray-700 text-xs'}>
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </CardContent>
                </Card>

                {/* Admin Notes */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Admin Notes</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col h-full">
                    <textarea
                      className="w-full flex-1 border rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[240px]"
                      placeholder="Add internal notes about this applicant…"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                    />
                    <Button className="w-full mt-3" onClick={handleSaveChecklist} disabled={savingChecklist}>
                      {savingChecklist ? 'Saving…' : 'Save Checklist & Notes'}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Activity Log */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" /> Activity Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {eventsLoading ? (
                    <p className="text-xs text-muted-foreground">Loading…</p>
                  ) : events.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No activity recorded yet.</p>
                  ) : (
                    <div className="relative pl-5 space-y-4 border-l-2 border-gray-100">
                      {[...events].reverse().map((ev) => (
                        <EventItem key={ev.id} event={ev} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Internal Notes */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Pencil className="w-4 h-4 text-primary" /> Internal Notes
                    <span className="text-xs text-muted-foreground font-normal ml-1">(visible to admins only)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(app.internal_notes || []).length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No internal notes yet.</p>
                  )}
                  {(app.internal_notes || []).map(note => (
                    <div key={note.id} className="flex items-start justify-between gap-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm whitespace-pre-wrap">{note.text}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {note.author_email || 'Admin'} · {fmtDateTime(note.created_at)}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 shrink-0" onClick={() => handleDeleteNote(note.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <textarea
                      className="flex-1 border rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[72px]"
                      placeholder="Add an internal note…"
                      value={internalNoteText}
                      onChange={e => setInternalNoteText(e.target.value)}
                    />
                    <Button size="sm" className="self-end" onClick={handleAddInternalNote} disabled={addingNote || !internalNoteText.trim()}>
                      {addingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {checkedCount === checklistItems.length && (
                <Alert className="border-green-400 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700 text-sm ml-1">
                    All checklist items verified. Application review is complete.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {/* ━━━━━━━━━━━ TEST ACTIVITY TAB ━━━━━━━━━━━ */}
          {activeTab === 'test_activity' && (
            <div className="space-y-5">
              {testActivityLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!testActivityLoading && !testActivity && (
                <div className="text-center py-12 text-muted-foreground">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No test activity recorded yet.</p>
                  <p className="text-xs mt-1">Assessment activity is available once the applicant has been sent an assessment link.</p>
                </div>
              )}
              {testActivity && (
                <>
                  {/* Integrity Summary */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="w-4 h-4 text-primary" /> Integrity Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="rounded-lg border p-3 text-center">
                          <div className="text-xl font-bold text-primary">{testActivity.violation_count ?? 0}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">Violations</div>
                        </div>
                        <div className="rounded-lg border p-3 text-center">
                          <div className="text-xl font-bold text-primary">
                            {testActivity.total_duration_seconds != null
                              ? `${Math.floor(testActivity.total_duration_seconds / 60)}m ${testActivity.total_duration_seconds % 60}s`
                              : '—'}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">Duration</div>
                        </div>
                        <div className="rounded-lg border p-3 text-center">
                          <div className="text-xl font-bold text-primary">{testActivity.score?.toFixed(0) ?? '—'}%</div>
                          <div className="text-xs text-muted-foreground mt-0.5">Score</div>
                        </div>
                        <div className="rounded-lg border p-3 text-center">
                          {(() => {
                            const lvl = testActivity.integrity_level || 'clean';
                            const cfg = { clean: { label: 'Clean', color: 'text-green-600', Icon: ShieldCheck }, watch: { label: 'Watch', color: 'text-amber-600', Icon: Shield }, suspicious: { label: 'Suspicious', color: 'text-orange-600', Icon: ShieldAlert }, flagged: { label: 'Flagged', color: 'text-red-600', Icon: ShieldAlert }, disqualified: { label: 'DQ', color: 'text-gray-500', Icon: ShieldAlert } };
                            const { label, color, Icon } = cfg[lvl] || cfg.clean;
                            return (
                              <>
                                <Icon className={`w-6 h-6 mx-auto ${color}`} />
                                <div className={`text-xs font-semibold mt-0.5 ${color}`}>{label}</div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Timestamps */}
                      {(testActivity.started_at || testActivity.completed_at) && (
                        <div className="flex gap-4 text-xs text-muted-foreground mb-3">
                          {testActivity.started_at && (
                            <span>Started: <span className="font-medium text-gray-700">{new Date(testActivity.started_at).toLocaleString('en-GB')}</span></span>
                          )}
                          {testActivity.completed_at && (
                            <span>Submitted: <span className="font-medium text-gray-700">{new Date(testActivity.completed_at).toLocaleString('en-GB')}</span></span>
                          )}
                          {testActivity.auto_submitted && (
                            <span className="text-red-600 font-semibold">Auto-submitted (violations)</span>
                          )}
                        </div>
                      )}

                      {/* Disqualify / Approve controls */}
                      {(testActivity.integrity_level === 'flagged' || testActivity.integrity_level === 'suspicious') && !app.test_disqualified && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            disabled={testReviewing}
                            onClick={() => handleTestReview('disqualify')}
                          >
                            <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Disqualify
                          </Button>
                          <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" disabled={testReviewing} onClick={() => handleTestReview('approve')}>
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Mark Clean
                          </Button>
                        </div>
                      )}
                      {app.test_disqualified && (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-200">
                          <ShieldAlert className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-700 font-medium">Test disqualified</span>
                          {app.test_review_note && <span className="text-xs text-red-600">— {app.test_review_note}</span>}
                          <Button size="sm" variant="ghost" className="ml-auto text-xs text-muted-foreground" disabled={testReviewing} onClick={() => handleTestReview('approve')}>
                            Reinstate
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Violations Log */}
                  {testActivity.violations?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-500" /> Violations ({testActivity.violations.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {testActivity.violations.map((v, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0 text-xs">
                              <span className="text-orange-700 font-medium">{v.type?.replace(/_/g, ' ')}</span>
                              <span className="text-muted-foreground">{new Date(v.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Passage Breakdown */}
                  {testActivity.passage_analysis?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Clock className="w-4 h-4 text-primary" /> Passage Breakdown
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {testActivity.passage_analysis.map((p, i) => (
                            <PassageActivityBlock key={i} passage={p} index={i} />
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ── Outcome Decision ── */}
                  {app.status === 'test_completed' && (
                    <Card className="border-2 border-primary/20">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-primary" /> Outcome Decision
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Based on the assessment results above, make your admission decision. An outcome email will be sent automatically.
                        </p>
                        <div className="flex gap-3">
                          <Button
                            className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                            onClick={handleAcceptApplicant}
                          >
                            <CheckCircle className="w-4 h-4 mr-2" /> Accept
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                            onClick={handleRejectApplicant}
                          >
                            <XCircle className="w-4 h-4 mr-2" /> Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}

          {/* ━━━━━━━━━━━ EMAILS TAB ━━━━━━━━━━━ */}
          {activeTab === 'emails' && (
            <div className="space-y-5">
              {/* Manual Send */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Send className="w-4 h-4 text-primary" /> Send Email Manually
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      {
                        key: 'application_received_eligible',
                        label: 'Application Received (Eligible)',
                        color: 'border-blue-200 text-blue-700 hover:bg-blue-50',
                        tip: 'Sent automatically on submission when applicant meets eligibility criteria. Resend if they did not receive it.',
                      },
                      {
                        key: 'application_received_ineligible',
                        label: 'Application Received (Ineligible)',
                        color: 'border-red-200 text-red-700 hover:bg-red-50',
                        tip: 'Sent automatically on submission when applicant does not meet eligibility criteria. Lists the unmet criteria.',
                      },
                      {
                        key: 'shortlisted',
                        label: 'Shortlisted for Academic Assessment',
                        color: 'border-purple-200 text-purple-700 hover:bg-purple-50',
                        tip: 'Send after closing review when the applicant is shortlisted. Informs them they met eligibility and will be invited to an academic assessment.',
                      },
                      {
                        key: 'not_shortlisted',
                        label: 'Not Shortlisted',
                        color: 'border-orange-200 text-orange-700 hover:bg-orange-50',
                        tip: 'Send after closing review when the applicant is not shortlisted. Informs them they will not proceed to the assessment stage.',
                      },
                      {
                        key: 'test_link',
                        label: 'Academic Assessment Link',
                        color: 'border-indigo-200 text-indigo-700 hover:bg-indigo-50',
                        tip: 'Send when the assessment date is confirmed. Contains the test link, date, time, mode, and login instructions. Status moves to Assessment Invited.',
                      },
                      {
                        key: 'test_submitted',
                        label: 'Application Process Completed',
                        color: 'border-cyan-200 text-cyan-700 hover:bg-cyan-50',
                        tip: 'Send after the applicant completes the assessment. Confirms the process is complete and the team is reviewing results.',
                      },
                      {
                        key: 'acceptance',
                        label: 'Admission Confirmed',
                        color: 'border-green-200 text-green-700 hover:bg-green-50',
                        tip: 'Send when the applicant is accepted. Includes cohort start date, programme details, and next steps. Status moves to Accepted.',
                      },
                      {
                        key: 'accepted_cohort_2_3',
                        label: 'Accepted — Cohort Dates TBD',
                        color: 'border-violet-200 text-violet-700 hover:bg-violet-50',
                        tip: 'Send for applicants accepted into Cohort 2 or 3 where start dates are not yet confirmed. Sets expectation without committing to specific dates.',
                      },
                      {
                        key: 'test_failed',
                        label: 'Assessment Unsuccessful',
                        color: 'border-red-200 text-red-700 hover:bg-red-50',
                        tip: 'Send when the applicant did not meet the pass mark in the academic assessment. Informs them they have not been selected.',
                      },
                    ].map(({ key, label, color, tip }) => {
                      const lastSent = emailLogs.find(l => l.template_key === key);
                      return (
                        <TooltipProvider key={key} delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                disabled={sendingEmail === key}
                                onClick={() => handleSendEmail(key, label)}
                                className={`flex items-start justify-between border rounded-lg px-3 py-2.5 text-sm text-left transition-colors disabled:opacity-50 ${color}`}
                              >
                                <div className="min-w-0">
                                  <p className="font-medium text-xs flex items-center gap-1">
                                    {label}
                                    <HelpCircle className="w-3 h-3 opacity-40 shrink-0" />
                                  </p>
                                  {lastSent && (
                                    <p className="text-xs opacity-60 mt-0.5">
                                      Last sent {fmtDateTime(lastSent.created_at)}
                                    </p>
                                  )}
                                </div>
                                <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 ml-2 opacity-70" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs text-xs">
                              {tip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Email History */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="w-4 h-4 text-primary" /> Email History
                    </CardTitle>
                    <button onClick={() => fetchEmailLogs(app?.email)} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> Refresh
                    </button>
                  </div>
                </CardHeader>
                <CardContent>
                  {emailLogsLoading ? (
                    <p className="text-xs text-muted-foreground">Loading…</p>
                  ) : emailLogs.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No emails sent yet for this application.</p>
                  ) : (
                    <div className="space-y-2">
                      {emailLogs.map(log => (
                        <div key={log.id} className="border rounded-lg px-3 py-2.5 text-xs bg-gray-50">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{log.subject}</p>
                              <p className="text-muted-foreground mt-0.5">
                                <span className="bg-gray-200 px-1.5 py-0.5 rounded font-mono">{log.template_key || 'custom'}</span>
                                {' → '}{log.to_email}
                              </p>
                            </div>
                            <EmailStatusBadge status={log.status} />
                          </div>
                          <div className="flex items-center gap-3 mt-1.5 text-muted-foreground">
                            <span>Sent {fmtDateTime(log.created_at)}</span>
                            {log.delivered_at && <span>· Delivered {new Date(log.delivered_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
                            {log.opened_at && <span>· Opened {new Date(log.opened_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
                          </div>
                          {log.bounce_reason && <p className="text-red-600 mt-1">Bounce: {log.bounce_reason}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* ── Right: Sticky Admin Panel (1 col) ─────────────────────────────── */}
        <div className="space-y-5 lg:sticky lg:top-6 lg:self-start">

          {/* Pipeline Timeline */}
          {(() => {
            const timeline = buildTimeline(app, events, emailLogs);
            return (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Pipeline</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <ol className="relative">
                    {timeline.map((stage, i) => {
                      const isLast = i === timeline.length - 1;
                      const isDone = stage.state === 'done';
                      const isCurrent = stage.state === 'current';
                      const isFail = stage.variant === 'fail';

                      const dotClass = isDone
                        ? (isFail ? 'bg-red-500 border-red-500' : 'bg-green-500 border-green-500')
                        : isCurrent
                        ? 'bg-white border-blue-500 border-2'
                        : 'bg-white border-gray-300 border-2';

                      const labelClass = isDone
                        ? (isFail ? 'text-red-700 font-medium' : 'text-gray-700 font-medium')
                        : isCurrent
                        ? 'text-blue-700 font-semibold'
                        : 'text-gray-400';

                      return (
                        <li key={i} className="flex gap-3 pb-4 last:pb-0">
                          <div className="flex flex-col items-center">
                            <div className={`w-3 h-3 rounded-full mt-0.5 shrink-0 flex items-center justify-center ${dotClass}`}>
                              {isDone && !isFail && (
                                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 12 12">
                                  <path d="M10 3L5 8.5 2 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                                </svg>
                              )}
                              {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                            </div>
                            {!isLast && <div className={`w-px flex-1 mt-1 ${isDone ? 'bg-green-300' : 'bg-gray-200'}`} />}
                          </div>
                          <div className="min-w-0 pb-0">
                            <p className={`text-xs leading-snug ${labelClass}`}>{stage.label}</p>
                            {stage.date && (
                              <p className="text-[10px] text-muted-foreground mt-0.5">{stage.date}</p>
                            )}
                            {isCurrent && !stage.date && (
                              <p className="text-[10px] text-blue-500 mt-0.5">In progress</p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </CardContent>
              </Card>
            );
          })()}

          {/* Primary Actions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Actions</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs text-orange-600 hover:bg-orange-50 h-7 px-2" onClick={() => { setOverrideStatus(app.status); setOverrideModal(true); }}>
                  Override
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {/* Override notice */}
              {app.override_reason && (
                <div className="flex items-start gap-1.5 p-2 rounded-lg bg-amber-50 border border-amber-200 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800"><span className="font-semibold">Overridden</span> by {app.override_by} — {app.override_reason}</p>
                </div>
              )}

              {/* Flag control */}
              {app.flagged ? (
                <div className="p-2 rounded-lg bg-red-50 border border-red-200 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Flag className="w-3.5 h-3.5 text-red-600 shrink-0" />
                    <span className="text-xs font-semibold text-red-800">Flagged</span>
                    <button onClick={handleUnflag} className="ml-auto text-[10px] text-red-500 hover:text-red-700 underline">Remove</button>
                  </div>
                  {(app.flag_notes && app.flag_notes.length > 0) ? (
                    <div className="space-y-0.5">
                      {[...app.flag_notes].reverse().map((note, i) => (
                        editingNote?.created_at === note.created_at ? (
                          <div key={i} className="flex items-center gap-1">
                            <Input
                              value={editingNote.text}
                              onChange={e => setEditingNote(prev => ({ ...prev, text: e.target.value }))}
                              className="h-6 text-[11px] flex-1 py-0"
                              onKeyDown={e => { if (e.key === 'Enter') handleEditFlagNote(); if (e.key === 'Escape') setEditingNote(null); }}
                              autoFocus
                            />
                            <button onClick={handleEditFlagNote} className="text-[10px] text-red-600 hover:text-red-800 font-medium flex-shrink-0">Save</button>
                            <button onClick={() => setEditingNote(null)} className="text-[10px] text-gray-400 hover:text-gray-600 flex-shrink-0">✕</button>
                          </div>
                        ) : (
                          <div key={i} className="flex items-start gap-1">
                            <p className="text-[11px] text-red-700 leading-tight flex-1">
                              <span className="font-medium">{formatFlagDate(note.created_at)}</span> — {note.text}
                            </p>
                            <button onClick={() => setEditingNote({ created_at: note.created_at, text: note.text })} className="flex-shrink-0 text-red-300 hover:text-red-600"><Pencil className="w-2.5 h-2.5" /></button>
                            <button onClick={() => handleDeleteFlagNote(note.created_at)} className="text-[11px] text-red-300 hover:text-red-700 flex-shrink-0 leading-tight">×</button>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    app.flag_reason && <p className="text-[11px] text-red-700 leading-tight">{app.flag_reason}</p>
                  )}
                  <div className="space-y-1 pt-0.5 border-t border-red-200">
                    <Input
                      placeholder="Add another note..."
                      value={flagReason}
                      onChange={e => setFlagReason(e.target.value)}
                      className="h-7 text-xs"
                      onKeyDown={e => e.key === 'Enter' && handleFlag()}
                    />
                    <Button size="sm" className="w-full h-6 text-[11px] bg-red-600 hover:bg-red-700 text-white" onClick={handleFlag} disabled={!flagReason.trim()}>
                      Add Note
                    </Button>
                  </div>
                </div>
              ) : showFlagInput ? (
                <div className="space-y-1.5">
                  <Input
                    placeholder="Reason for flagging..."
                    value={flagReason}
                    onChange={e => setFlagReason(e.target.value)}
                    className="h-8 text-xs"
                    onKeyDown={e => e.key === 'Enter' && handleFlag()}
                    autoFocus
                  />
                  <div className="flex gap-1.5">
                    <Button size="sm" className="flex-1 h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={handleFlag} disabled={!flagReason.trim()}>
                      Confirm Flag
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowFlagInput(false); setFlagReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 text-xs h-8" onClick={() => setShowFlagInput(true)}>
                  <Flag className="w-3.5 h-3.5 mr-1.5" /> Flag Application
                </Button>
              )}

              {/* Blacklist control */}
              {app.blacklisted ? (
                <div className="p-2 rounded-lg bg-orange-50 border border-orange-300 space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Ban className="w-3.5 h-3.5 text-orange-700 shrink-0" />
                    <span className="text-xs font-semibold text-orange-800">Blacklisted</span>
                    <button onClick={handleUnblacklist} className="ml-auto text-[10px] text-orange-600 hover:text-orange-800 underline">Remove</button>
                  </div>
                  {app.blacklist_reason && (
                    <p className="text-[11px] text-orange-700 leading-tight">{app.blacklist_reason}</p>
                  )}
                  {app.blacklisted_by && (
                    <p className="text-[10px] text-orange-500">{app.blacklisted_by} · {app.blacklisted_at ? new Date(app.blacklisted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</p>
                  )}
                </div>
              ) : showBlacklistInput ? (
                <div className="space-y-1.5">
                  <Input
                    placeholder="Reason for blacklisting (e.g. No-show after acceptance)..."
                    value={blacklistReason}
                    onChange={e => setBlacklistReason(e.target.value)}
                    className="h-8 text-xs"
                    onKeyDown={e => e.key === 'Enter' && handleBlacklist()}
                    autoFocus
                  />
                  <div className="flex gap-1.5">
                    <Button size="sm" className="flex-1 h-7 text-xs bg-orange-600 hover:bg-orange-700 text-white" onClick={handleBlacklist} disabled={!blacklistReason.trim()}>
                      Confirm Blacklist
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowBlacklistInput(false); setBlacklistReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="w-full border-orange-200 text-orange-700 hover:bg-orange-50 text-xs h-8" onClick={() => setShowBlacklistInput(true)}>
                  <Ban className="w-3.5 h-3.5 mr-1.5" /> Blacklist Applicant
                </Button>
              )}

              {/* Shortlist / Decline — only for received/pending (pre-review) */}
              {['received', 'pending'].includes(app.status) && app.eligible && (
                <>
                  {app.shortlisted !== true && (
                    <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white" onClick={() => handleShortlist(true)}>
                      <Star className="w-4 h-4 mr-2 fill-white" /> Shortlist
                    </Button>
                  )}
                  {app.shortlisted !== false && (
                    <Button variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleShortlist(false)}>
                      <XCircle className="w-4 h-4 mr-2" /> Decline
                    </Button>
                  )}
                  {app.shortlisted === true && (
                    <Button variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => handleShortlist(false)}>
                      Remove from Shortlist
                    </Button>
                  )}
                  {app.shortlisted === false && (
                    <Button variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => handleShortlist(true)}>
                      Undo Decline
                    </Button>
                  )}
                  <div className="border-t my-1" />
                </>
              )}

              {/* Send Test Link */}
              {['shortlisted', 'screening', 'pending', 'received'].includes(app.status) && (
                <Button variant="outline" className="w-full" onClick={handleSendTest}>
                  <Send className="w-4 h-4 mr-2" /> Send Academic Assessment
                </Button>
              )}

              {/* Outcome Decision — directs to test tab */}
              {app.status === 'test_completed' && (
                <Button
                  className="w-full bg-primary hover:bg-primary/90 text-white"
                  onClick={() => setActiveTab('test_activity')}
                >
                  <Activity className="w-4 h-4 mr-2" /> Review Assessment &amp; Decide
                </Button>
              )}

              {/* Resend Test Link */}
              {['test_invited', 'test_sent', 'test_completed', 'withdrawn', 'not_shortlisted', 'not_selected'].includes(app.status) && (
                <Button variant="outline" className="w-full text-purple-700 border-purple-300 hover:bg-purple-50" onClick={handleResendTest}>
                  <Send className="w-4 h-4 mr-2" /> {['test_completed', 'withdrawn', 'not_shortlisted', 'not_selected'].includes(app.status) ? 'Re-invite to Assessment' : 'Resend Assessment Link'}
                </Button>
              )}

              {/* View Test Responses */}
              {['test_invited', 'test_completed', 'accepted', 'selected_tba', 'not_selected', 'on_hold', 'enrolled', 'test_sent', 'rejected'].includes(app.status) && (
                <Button variant="outline" className="w-full" onClick={handleViewTest}>
                  <FileText className="w-4 h-4 mr-2" /> View Test Responses
                </Button>
              )}

              {/* Enroll to Cohort (direct) */}
              {app.status === 'accepted' && (
                <Button className="w-full bg-green-700 hover:bg-green-800" onClick={() => { setSelectedCohort(cohorts[0]?.id || ''); setEnrollDialog(true); }}>
                  <UserCheck className="w-4 h-4 mr-2" /> Enroll as Student — Cohort 1
                </Button>
              )}

              {/* Walk-in: manually confirm + enroll for withdrawn eligible applicants */}
              {app.status === 'withdrawn' && app.eligible && (
                <Button className="w-full bg-green-700 hover:bg-green-800" onClick={handleManualConfirmAndEnroll}>
                  <UserCheck className="w-4 h-4 mr-2" /> Manually Confirm &amp; Enroll
                </Button>
              )}

              {/* Withdraw enrolled student */}
              {app.status === 'enrolled' && (
                <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50" onClick={handleWithdraw}>
                  <XCircle className="w-4 h-4 mr-2" /> Mark as Withdrawn
                </Button>
              )}

              {/* Hold — Dates TBA */}
              {app.status === 'accepted' && (
                <Button variant="outline" className="w-full border-violet-300 text-violet-700 hover:bg-violet-50" onClick={handleHoldTBA}>
                  <Clock className="w-4 h-4 mr-2" /> Hold — Dates TBA (Cohort 2/3)
                </Button>
              )}

              {/* Assign to cohort for TBA-held applicants */}
              {app.status === 'selected_tba' && (
                <Button className="w-full bg-violet-700 hover:bg-violet-800" onClick={() => { setSelectedCohort(cohorts[0]?.id || ''); setEnrollDialog(true); }}>
                  <UserCheck className="w-4 h-4 mr-2" /> Assign to Cohort &amp; Enroll
                </Button>
              )}

              <Button variant="outline" className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={() => { setReviewDocIndex(0); setReviewDrawer(true); }}>
                <Eye className="w-4 h-4 mr-2" /> Review Documents (Inline)
              </Button>
            </CardContent>
          </Card>

          {/* Score Summary */}
          {(app.score != null || app.aptitude_score != null || app.final_score != null) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {app.score != null && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Application</span>
                    <span className={`font-bold text-sm ${scoreColor(app.score)}`}>{app.score.toFixed(1)} / 100</span>
                  </div>
                )}
                {app.aptitude_score != null && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Academic Assessment</span>
                    <span className={`font-bold text-sm ${scoreColor(app.aptitude_score)}`}>{app.aptitude_score.toFixed(1)}%</span>
                  </div>
                )}
                {app.final_score != null && (
                  <>
                    <div className="border-t pt-2 flex justify-between items-center">
                      <span className="text-sm font-semibold">Final Score</span>
                      <span className={`font-bold text-lg ${scoreColor(app.final_score)}`}>{app.final_score.toFixed(1)}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Review Progress */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Review Progress</CardTitle>
                <span className={`text-xs font-semibold ${checkedCount === checklistItems.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {checkedCount}/{checklistItems.length}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${checkedCount === checklistItems.length ? 'bg-green-500' : 'bg-primary'}`}
                  style={{ width: `${(checkedCount / checklistItems.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {checkedCount === checklistItems.length
                  ? 'All items verified'
                  : `${checklistItems.length - checkedCount} items pending`}
              </p>
              <Button variant="ghost" size="sm" className="w-full mt-2 text-xs text-muted-foreground" onClick={() => setActiveTab('review')}>
                Open Review Tab →
              </Button>
            </CardContent>
          </Card>

          {/* Intake Assignment */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Intake</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {app.intake_id ? (
                <p className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-3 py-2 font-medium">
                  {intakePeriods.find(i => i.id === app.intake_id)?.name || app.intake_id}
                </p>
              ) : (
                <p className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg px-3 py-2">
                  Not assigned to any intake
                </p>
              )}
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                value={selectedIntakeId}
                onChange={e => setSelectedIntakeId(e.target.value)}
              >
                <option value="">— No intake —</option>
                {intakePeriods.map(ip => (
                  <option key={ip.id} value={ip.id}>{ip.name} ({ip.status})</option>
                ))}
              </select>
              <Button
                className="w-full"
                size="sm"
                onClick={handleAssignIntake}
                disabled={savingIntake || selectedIntakeId === (app.intake_id || '')}
              >
                {savingIntake ? 'Saving…' : 'Assign to Intake'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Add Document Dialog ─────────────────────────────────────────────── */}
      <Dialog open={addDocDialog} onOpenChange={setAddDocDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
            <DialogDescription>Upload an additional document. New tags are saved globally for reuse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Tag <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  placeholder="Type or select a tag…"
                  value={addDocForm.tagInput}
                  onChange={e => handleTagInput(e.target.value)}
                  onFocus={() => { setTagSuggestions(addDocForm.tagInput ? docTags.filter(t => t.toLowerCase().includes(addDocForm.tagInput.toLowerCase())) : docTags); setShowTagSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
                  autoComplete="off"
                />
                {showTagSuggestions && tagSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {tagSuggestions.map(t => (
                      <div
                        key={t}
                        className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        onMouseDown={() => { setAddDocForm(f => ({ ...f, tag: t, tagInput: t })); setShowTagSuggestions(false); }}
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {addDocForm.tag && !docTags.includes(addDocForm.tag) && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><Plus className="w-3 h-3" /> New tag "{addDocForm.tag}" will be saved globally</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>File <span className="text-red-500">*</span></Label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleAddDocFileChange}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary/90 cursor-pointer"
              />
              {addDocForm.filename && <p className="text-xs text-muted-foreground">Selected: {addDocForm.filename}</p>}
            </div>
            <div className="space-y-1">
              <Label>Note <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input
                placeholder="e.g. Requested after interview"
                value={addDocForm.note}
                onChange={e => setAddDocForm(f => ({ ...f, note: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDocDialog(false)}>Cancel</Button>
            <Button onClick={handleAddDocument} disabled={addDocLoading || !addDocForm.tag || !addDocForm.file_data}>
              {addDocLoading ? 'Uploading…' : <><Upload className="w-3.5 h-3.5 mr-1" /> Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Document Dialog ───────────────────────────────────────────── */}
      <Dialog open={editDocDialog} onOpenChange={setEditDocDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>Update the tag or note for "{editDocTarget?.filename}".</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Tag <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input
                  placeholder="Type or select a tag…"
                  value={editDocForm.tagInput}
                  onChange={e => handleEditTagInput(e.target.value)}
                  onFocus={() => { setEditTagSuggestions(editDocForm.tagInput ? docTags.filter(t => t.toLowerCase().includes(editDocForm.tagInput.toLowerCase())) : docTags); setShowEditTagSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowEditTagSuggestions(false), 150)}
                  autoComplete="off"
                />
                {showEditTagSuggestions && editTagSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {editTagSuggestions.map(t => (
                      <div
                        key={t}
                        className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                        onMouseDown={() => { setEditDocForm(f => ({ ...f, tag: t, tagInput: t })); setShowEditTagSuggestions(false); }}
                      >
                        {t}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {editDocForm.tag && !docTags.includes(editDocForm.tag) && (
                <p className="text-xs text-amber-600 flex items-center gap-1"><Plus className="w-3 h-3" /> New tag will be saved globally</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Note <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input
                placeholder="e.g. Replacement document"
                value={editDocForm.note}
                onChange={e => setEditDocForm(f => ({ ...f, note: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDocDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateDocument} disabled={editDocLoading || !editDocForm.tag.trim()}>
              {editDocLoading ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Enroll Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={enrollDialog} onOpenChange={setEnrollDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enroll as Student</DialogTitle>
            <DialogDescription>Select a cohort to enroll {app.full_name} into.</DialogDescription>
          </DialogHeader>
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={selectedCohort}
            onChange={e => setSelectedCohort(e.target.value)}
          >
            <option value="">— Select cohort —</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollDialog(false)}>Cancel</Button>
            <Button onClick={handleEnroll} disabled={enrolling || !selectedCohort}>
              {enrolling ? 'Enrolling…' : 'Enroll'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Test Responses Dialog ─────────────────────────────────────────── */}
      <Dialog open={viewTestDialog} onOpenChange={setViewTestDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Academic Assessment Responses</DialogTitle>
            <DialogDescription>
              {testData?.completed
                ? `Score: ${testData.score?.toFixed(1)}% · Completed ${fmtDateTime(testData.completed_at)}`
                : 'Test not yet completed'}
              {testData?.flagged && <span className="ml-2 text-red-600 font-semibold">⚠ Flagged</span>}
            </DialogDescription>
          </DialogHeader>
          {testData && (
            <div className="space-y-6">
              {testData.violations?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-orange-700 mb-1">Violations ({testData.violation_count})</p>
                  <div className="space-y-0.5 max-h-24 overflow-y-auto">
                    {testData.violations.map((v, i) => (
                      <p key={i} className="text-xs text-orange-600">{v.type} — {new Date(v.timestamp).toLocaleTimeString()}</p>
                    ))}
                  </div>
                </div>
              )}
              {testData.passages?.length > 0 ? (
                testData.passages.map((passage, pi) => (
                  <PassageResult key={passage.passage_id || pi} passage={passage} answers={testData.answers} timings={testData.passage_timings} />
                ))
              ) : (
                testData.questions?.map((q, qi) => {
                  const answer = testData.answers?.find(a => a.question_id === q.id);
                  const OPTS = ['A','B','C','D','E'];
                  return (
                    <div key={q.id} className="border rounded-lg p-4">
                      <p className="font-medium text-sm mb-3">Q{qi + 1}. {q.text || q.question}</p>
                      <div className="space-y-1">
                        {(q.options || []).map((opt, oi) => {
                          const letter = OPTS[oi];
                          const isCorrect = letter === q.correct_answer;
                          const isSelected = answer?.selected_answer === letter;
                          return (
                            <div key={oi} className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 ${
                              isCorrect ? 'bg-green-100 text-green-800 font-medium' :
                              isSelected ? 'bg-red-100 text-red-700' : 'bg-gray-50'
                            }`}>
                              {isCorrect ? <CheckCircle className="w-3 h-3" /> : isSelected ? <XCircle className="w-3 h-3" /> : <span className="w-3 h-3" />}
                              <span className="font-semibold mr-1">{letter}.</span> {opt}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Edit Application Dialog ─────────────────────────────────────────── */}
      <Dialog open={editMode} onOpenChange={v => { if (!v) setEditMode(false); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>
              Correct personal or academic details. If GPA or date of birth changes, eligibility and score will be recalculated automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Personal Details</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'full_name', label: 'Full Name' },
                  { key: 'email', label: 'Email', type: 'email' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'date_of_birth', label: 'Date of Birth', type: 'date' },
                  { key: 'nationality', label: 'Nationality' },
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label}</Label>
                    <Input type={f.type || 'text'} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} className="text-sm h-8" />
                  </div>
                ))}
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input value={editForm.address || ''} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} className="text-sm h-8" />
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">LLB Details</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">LLB Status</Label>
                  <select className="w-full border rounded-md px-2 py-1.5 text-sm bg-white" value={editForm.llb_status || ''} onChange={e => setEditForm(p => ({ ...p, llb_status: e.target.value }))}>
                    <option value="completed">Completed</option>
                    <option value="to_be_awarded">To Be Awarded</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">LLB GPA</Label>
                  <Input type="number" step="0.01" min="0" max="5" value={editForm.llb_gpa ?? ''} onChange={e => setEditForm(p => ({ ...p, llb_gpa: e.target.value }))} className="text-sm h-8" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GPA Scale</Label>
                  <select className="w-full border rounded-md px-2 py-1.5 text-sm bg-white" value={editForm.llb_gpa_scale || 5} onChange={e => setEditForm(p => ({ ...p, llb_gpa_scale: parseInt(e.target.value) }))}>
                    <option value={4}>4.0</option>
                    <option value={5}>5.0</option>
                  </select>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Referee 1</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'referee1_name', label: 'Name' },
                  { key: 'referee1_title', label: 'Title' },
                  { key: 'referee1_institution', label: 'Institution' },
                  { key: 'referee1_email', label: 'Email', type: 'email' },
                  { key: 'referee1_telephone', label: 'Phone' },
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label}</Label>
                    <Input type={f.type || 'text'} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} className="text-sm h-8" />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Referee 2 (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'referee2_name', label: 'Name' },
                  { key: 'referee2_title', label: 'Title' },
                  { key: 'referee2_institution', label: 'Institution' },
                  { key: 'referee2_email', label: 'Email', type: 'email' },
                  { key: 'referee2_telephone', label: 'Phone' },
                ].map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label}</Label>
                    <Input type={f.type || 'text'} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} className="text-sm h-8" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMode(false)} disabled={savingEdit}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={savingEdit}>
              {savingEdit ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Email Compose Modal ─────────────────────────────────────────────── */}
      <Dialog open={!!emailComposeModal} onOpenChange={open => { if (!open) setEmailComposeModal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> Send Email: {emailComposeModal?.label}
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const fields = TEMPLATE_PLACEHOLDERS[emailComposeModal?.key] || [];
                return fields.length > 0
                  ? 'Review and edit values before sending. Fields marked "auto-filled" are pulled from the intake — override only if needed.'
                  : 'Click Send to dispatch this email to the applicant.';
              })()}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const fields = TEMPLATE_PLACEHOLDERS[emailComposeModal?.key] || [];
            return fields.length > 0 ? (
              <div className="space-y-3 py-2">
                {fields.map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label>{f.label}</Label>
                    <Input
                      value={emailComposeValues[f.key] ?? ''}
                      onChange={e => setEmailComposeValues(v => ({ ...v, [f.key]: e.target.value }))}
                      placeholder={f.hint || f.label}
                    />
                    {f.hint && (
                      <p className="text-xs text-muted-foreground">{f.hint}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-2 text-sm text-muted-foreground">
                All values are auto-filled from the applicant and intake records. Click Send to dispatch.
              </p>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailComposeModal(null)}>Cancel</Button>
            <Button
              onClick={handleSendEmailConfirm}
              disabled={!!sendingEmail}
              className="bg-primary text-white hover:bg-primary/90"
            >
              {sendingEmail ? 'Sending…' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Override Status Dialog ──────────────────────────────────────────── */}
      <Dialog open={overrideModal} onOpenChange={setOverrideModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Override Application Status</DialogTitle>
            <DialogDescription>
              Manually change the status. An audit event will be logged with your name and reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>New Status <span className="text-red-500">*</span></Label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                value={overrideStatus}
                onChange={e => setOverrideStatus(e.target.value)}
              >
                <option value="">— Select status —</option>
                {['received','ineligible','shortlisted','not_shortlisted','test_invited','test_completed','accepted','not_selected','on_hold','enrolled'].map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Reason <span className="text-red-500">*</span></Label>
              <textarea
                className="w-full border rounded-lg p-3 text-sm resize-none min-h-[80px] focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Required — explain why this manual change is needed"
                value={overrideReason}
                onChange={e => setOverrideReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOverrideModal(false); setOverrideStatus(''); setOverrideReason(''); }}>Cancel</Button>
            <Button
              onClick={handleOverrideStatus}
              disabled={overriding || !overrideStatus || !overrideReason.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {overriding ? 'Overriding…' : 'Override Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Start Review Drawer ────────────────────────────────────────────── */}
      {reviewDrawer && app && (
        <ReviewDrawer
          app={app}
          additionalDocs={adminDocs}
          sections={checklistSections}
          checklist={checklist}
          onToggle={handleChecklistToggle}
          notes={notes}
          onNotesChange={setNotes}
          onSave={handleSaveChecklist}
          saving={savingChecklist}
          onClose={() => setReviewDrawer(false)}
          docIndex={reviewDocIndex}
          onDocChange={setReviewDocIndex}
        />
      )}
    </div>
  );
}

// ── Helper Components ─────────────────────────────────────────────────────────

function FieldV({ label, value, span = 1 }) {
  return (
    <div className={span === 2 ? 'col-span-2' : ''}>
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className="font-medium text-sm">{value ?? <span className="italic text-gray-400">N/A</span>}</p>
    </div>
  );
}

function EmailStatusBadge({ status }) {
  const map = {
    sent:      'bg-blue-100 text-blue-700',
    delivered: 'bg-green-100 text-green-700',
    failed:    'bg-red-100 text-red-700',
    bounced:   'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

function DeclarationRow({ checked, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${checked ? 'bg-green-100' : 'bg-red-50'}`}>
        {checked
          ? <CheckCircle className="w-3.5 h-3.5 text-green-600" />
          : <XCircle className="w-3.5 h-3.5 text-red-500" />}
      </div>
      <span className="text-sm">{label}</span>
    </div>
  );
}

const EVENT_META = {
  submitted:       { icon: '📝', label: 'Application Submitted', color: 'text-blue-600' },
  status_change:   { icon: '🔄', label: 'Status Changed',        color: 'text-purple-600' },
  email_sent:      { icon: '✉️', label: 'Email Sent',            color: 'text-green-600' },
  test_created:    { icon: '🧪', label: 'Assessment Created',    color: 'text-indigo-600' },
  test_opened:     { icon: '👁', label: 'Assessment Opened',     color: 'text-orange-500' },
  test_completed:  { icon: '✅', label: 'Assessment Completed',  color: 'text-green-700' },
  note_added:      { icon: '📌', label: 'Note Added',            color: 'text-gray-600' },
  checklist_saved: { icon: '☑️', label: 'Checklist Saved',       color: 'text-gray-600' },
  enrolled:        { icon: '🎓', label: 'Enrolled as Student',   color: 'text-teal-600' },
  shortlisted:     { icon: '⭐', label: 'Shortlist Updated',     color: 'text-yellow-600' },
  intake_assigned: { icon: '📋', label: 'Intake Assigned',       color: 'text-blue-500' },
};

function EventItem({ event }) {
  const meta = EVENT_META[event.event_type] || { icon: '•', label: event.event_type, color: 'text-gray-500' };
  const d = event.details || {};
  return (
    <div className="relative -ml-5 pl-6 text-xs">
      <span className="absolute left-[-5px] top-1 w-2.5 h-2.5 rounded-full bg-white border-2 border-gray-300" />
      <div className={`font-semibold ${meta.color}`}>{meta.icon} {meta.label}</div>
      {event.event_type === 'status_change' && d.from && (
        <div className="text-muted-foreground">{d.from} → {d.to}</div>
      )}
      {event.event_type === 'email_sent' && (
        <div className="text-muted-foreground">Template: {d.template} → {d.to}</div>
      )}
      {event.event_type === 'shortlisted' && (
        <div className="text-muted-foreground">{d.shortlisted ? 'Shortlisted ⭐' : 'Declined'}</div>
      )}
      {event.event_type === 'intake_assigned' && (
        <div className="text-muted-foreground">Intake ID: {d.intake_id || '(cleared)'}</div>
      )}
      <div className="text-muted-foreground mt-0.5">
        {event.actor_email && <span>{event.actor_email} · </span>}
        {fmtDateTime(event.created_at)}
      </div>
    </div>
  );
}

function PassageActivityBlock({ passage, index }) {
  const [expanded, setExpanded] = useState(false);
  const OPTS = ['A', 'B', 'C', 'D', 'E'];
  const { title, question_count, correct_count, duration_seconds, suspicious, questions = [] } = passage;

  const pct = question_count > 0 ? Math.round((correct_count / question_count) * 100) : 0;
  const timeLabel = duration_seconds != null
    ? duration_seconds >= 60
      ? `${Math.floor(duration_seconds / 60)}m ${duration_seconds % 60}s`
      : `${duration_seconds}s`
    : '—';

  return (
    <div className={`rounded-lg border ${suspicious ? 'border-orange-200 bg-orange-50/40' : 'border-gray-200 bg-gray-50/40'}`}>
      {/* Header row */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs font-bold text-muted-foreground shrink-0">P{index + 1}</span>
          <span className="font-medium text-sm truncate">{title || `Passage ${index + 1}`}</span>
          {suspicious && <span className="text-xs text-orange-600 font-semibold shrink-0">⚠ Fast</span>}
        </div>
        <div className="flex items-center gap-4 shrink-0 ml-3">
          <span className="text-xs text-muted-foreground">{timeLabel}</span>
          <span className={`text-xs font-semibold ${pct === 100 ? 'text-green-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
            {correct_count}/{question_count} correct
          </span>
          <span className="text-xs text-muted-foreground">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {/* Score bar */}
      <div className="px-4 pb-2">
        <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Expanded: per-question breakdown */}
      {expanded && questions.length > 0 && (
        <div className="px-4 pb-4 space-y-2 border-t border-gray-200 pt-3">
          {questions.map((q, qi) => {
            const selected = q.selected_answer;
            const correct = q.correct_answer;
            return (
              <div key={q.question_id} className={`rounded-md border p-3 text-xs ${q.is_correct ? 'border-green-200 bg-green-50' : selected ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
                <p className="font-medium text-gray-800 mb-2">
                  <span className="text-secondary font-bold mr-1">Q{qi + 1}.</span>
                  {q.question_text}
                </p>
                <div className="grid grid-cols-1 gap-1">
                  {(q.options || []).map((opt, oi) => {
                    const label = OPTS[oi] || String(oi);
                    const isSelected = selected === label;
                    const isCorrect = correct === label;
                    return (
                      <div key={oi} className={`flex items-center gap-2 px-2 py-1 rounded ${isCorrect ? 'bg-green-100 font-semibold' : isSelected && !isCorrect ? 'bg-red-100' : ''}`}>
                        <span className={`font-bold w-4 shrink-0 ${isCorrect ? 'text-green-700' : isSelected ? 'text-red-600' : 'text-muted-foreground'}`}>{label}.</span>
                        <span className={isCorrect ? 'text-green-800' : isSelected && !isCorrect ? 'text-red-700' : 'text-gray-700'}>{opt}</span>
                        {isSelected && !isCorrect && <span className="ml-auto text-red-500 font-bold shrink-0">✗ Selected</span>}
                        {isCorrect && <span className="ml-auto text-green-600 font-bold shrink-0">{isSelected ? '✓ Correct' : '✓ Answer'}</span>}
                      </div>
                    );
                  })}
                </div>
                {!selected && (
                  <p className="mt-1 text-amber-600 font-medium">Not answered</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PassageResult({ passage, answers, timings }) {
  const [expanded, setExpanded] = useState(false);
  const OPTS = ['A','B','C','D','E'];
  const timeSpent = timings?.[passage.passage_id];
  const questions = passage.questions || [];
  const correct = questions.filter(q => {
    const ans = answers?.find(a => a.question_id === q.id);
    return ans?.selected_answer === q.correct_answer;
  }).length;

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
        onClick={() => setExpanded(v => !v)}
      >
        <div>
          <span className="font-semibold text-sm">{passage.title || `Passage ${passage.passage_id}`}</span>
          <span className="text-xs text-muted-foreground ml-3">{correct}/{questions.length} correct</span>
          {timeSpent != null && <span className="text-xs text-muted-foreground ml-2">· {Math.round(timeSpent)}s</span>}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="p-4 space-y-4">
          {passage.text && (
            <div className="bg-blue-50 rounded-lg p-3 text-xs text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto border border-blue-100">
              {passage.text}
            </div>
          )}
          {questions.map((q, qi) => {
            const ans = answers?.find(a => a.question_id === q.id);
            return (
              <div key={q.id} className="space-y-1">
                <p className="text-sm font-medium">Q{qi + 1}. {q.text || q.question}</p>
                {(q.options || []).map((opt, oi) => {
                  const letter = OPTS[oi];
                  const isCorrect = letter === q.correct_answer;
                  const isSelected = ans?.selected_answer === letter;
                  return (
                    <div key={oi} className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 ${
                      isCorrect ? 'bg-green-100 text-green-800 font-medium' :
                      isSelected ? 'bg-red-100 text-red-700' : 'bg-gray-50'
                    }`}>
                      {isCorrect ? <CheckCircle className="w-3 h-3" /> : isSelected ? <XCircle className="w-3 h-3" /> : <span className="w-3 h-3" />}
                      <span className="font-semibold mr-1">{letter}.</span> {opt}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Review Drawer ─────────────────────────────────────────────────────────────

const REVIEW_DOCS = [
  { key: 'nida_passport_copy',      typeKey: 'nida_passport_copy_type',       nameKey: 'nida_passport_copy_filename',      label: 'NIDA / Passport Copy' },
  { key: 'additional_certificate',  typeKey: 'additional_certificate_type',   nameKey: 'additional_certificate_filename',  label: 'O-Level Certificate' },
  { key: 'a_level_certificate',     typeKey: 'a_level_certificate_type',      nameKey: 'a_level_certificate_filename',     label: 'A-Level Certificate' },
  { key: 'llb_certificate',         typeKey: 'llb_certificate_type',          nameKey: 'llb_certificate_filename',         label: 'LLB Certificate' },
];

// Derive MIME type from a file key's extension when no typeKey is stored
function fileTypeFromKey(src) {
  if (!src) return 'application/octet-stream';
  const ext = src.split('.').pop().toLowerCase().split('?')[0];
  return { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', pdf: 'application/pdf' }[ext] || 'application/octet-stream';
}

const REVIEW_SECTIONS = [
  { docIdx: 0, label: 'Personal Information', items: [
    { key: 'id_verified',      label: 'Name on ID matches application' },
    { key: 'id_age_confirmed', label: 'Age ≤ 27 confirmed from ID' },
  ]},
  { docIdx: 1, label: 'O-Level Results', items: [
    { key: 'o_level_cert',     label: 'Certificate is authentic' },
    { key: 'o_level_division', label: 'Division matches declared' },
  ]},
  { docIdx: 2, label: 'A-Level / LLB', items: [
    { key: 'a_level_cert',     label: 'Division 1 confirmed (A-Level)' },
    { key: 'llb_cert',         label: 'GPA ≥ 3.3 confirmed (LLB)' },
  ]},
  { docIdx: 3, label: 'Declaration Signature', items: [
    { key: 'signature_match',  label: 'Signature present and legible' },
  ]},
];

function ReviewDrawer({ app, additionalDocs = [], sections = DEFAULT_CHECKLIST_SECTIONS, checklist, onToggle, notes, onNotesChange, onSave, saving, onClose, docIndex, onDocChange }) {
  const extra = (() => { try { return JSON.parse(app.additional_qualifications); } catch { return {}; } })();
  const allItems = sections.flatMap(s => s.items);

  // Merge applicant docs + admin-uploaded docs into a unified list
  const applicantDocs = REVIEW_DOCS
    .filter(d => app[d.key])
    .map(d => {
      const src = app[d.key];
      const fileType = (d.typeKey && app[d.typeKey]) || fileTypeFromKey(src);
      return { key: d.key, label: d.label, src, fileType, isAdmin: false };
    });

  const adminDocsList = additionalDocs.map(d => ({
    key: `admin_${d.id}`,
    label: `${d.tag}${d.note ? ` — ${d.note}` : ''}`,
    src: d.file_data,
    fileType: d.file_type,
    isAdmin: true,
  }));

  const availableDocs = [...applicantDocs, ...adminDocsList];

  // Resolve file keys → authenticated object URLs so iframes can display them
  const [resolvedUrls, setResolvedUrls] = React.useState({});
  const [loadingDoc, setLoadingDoc] = React.useState(false);

  const resolveKey = React.useCallback((key) => {
    if (!key || !isFileKey(key)) return;
    setResolvedUrls(prev => {
      if (prev[key]) return prev; // already fetched
      fetchFileAsObjectUrl(key).then(url => {
        if (url) setResolvedUrls(p => ({ ...p, [key]: url }));
      });
      return prev;
    });
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const doc = availableDocs[docIndex] || availableDocs[0];
    if (!doc || !isFileKey(doc.src)) { setLoadingDoc(false); return; }
    if (resolvedUrls[doc.src]) return;
    setLoadingDoc(true);
    fetchFileAsObjectUrl(doc.src).then(url => {
      if (!cancelled && url) setResolvedUrls(prev => ({ ...prev, [doc.src]: url }));
    }).finally(() => { if (!cancelled) setLoadingDoc(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docIndex, app.id]);


  // Revoke object URLs on unmount to free memory
  React.useEffect(() => {
    return () => { Object.values(resolvedUrls).forEach(u => URL.revokeObjectURL(u)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentDoc = availableDocs[docIndex] || availableDocs[0];
  const resolvedSrc = currentDoc ? (resolvedUrls[currentDoc.src] || (isFileKey(currentDoc.src) ? null : currentDoc.src)) : null;
  const isImage = currentDoc?.fileType?.startsWith('image/');

  const checklistItems = sections.flatMap(s => s.items);
  const checkedCount = checklistItems.filter(i => checklist[i.key]).length;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-primary text-white shrink-0">
        <div className="flex items-center gap-3">
          <Eye className="w-5 h-5" />
          <span className="font-semibold">{app.full_name}</span>
          <span className="text-white/60 text-sm">— Document Review</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-white/70 text-xs">{checkedCount}/{checklistItems.length} items checked</span>
          <button onClick={onSave} disabled={saving} className="text-xs bg-white/10 hover:bg-white/20 border border-white/30 px-3 py-1.5 rounded transition-colors">
            {saving ? 'Saving…' : 'Save & Close'}
          </button>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xs px-3 py-1.5 rounded border border-white/30 transition-colors">
            ✕ Close
          </button>
        </div>
      </div>

      {/* ── 3-Column Body ── */}
      <div className="flex flex-1 overflow-hidden min-h-0">

        {/* ── Col 1: Checklist (fixed width) ── */}
        <div className="w-64 shrink-0 border-r flex flex-col bg-white">
          <div className="px-4 py-2.5 border-b bg-gray-50 shrink-0">
            <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Checklist</p>
            <div className="h-1 rounded-full bg-gray-200 mt-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${checkedCount === checklistItems.length ? 'bg-green-500' : 'bg-primary'}`}
                style={{ width: `${(checkedCount / checklistItems.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-4">
            {sections.map((section) => (
              <div key={section.id || section.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-wide text-primary">{section.label}</span>
                  {section.doc_idx != null && applicantDocs[section.doc_idx] && (
                    <button
                      className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-0.5"
                      onClick={() => onDocChange(section.doc_idx)}
                    >
                      <ExternalLink className="w-3 h-3" /> doc
                    </button>
                  )}
                </div>
                {section.items.map((item) => (
                  <button
                    key={item.key}
                    className="w-full flex items-start gap-2 text-left text-xs p-1.5 rounded hover:bg-gray-50 transition-colors"
                    onClick={() => onToggle(item.key)}
                  >
                    {checklist[item.key]
                      ? <CheckSquare className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                      : <Square className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />}
                    <span className={checklist[item.key] ? 'line-through text-green-700' : 'text-gray-700'}>
                      {item.label}
                    </span>
                  </button>
                ))}
                {section.items.length === 0 && (
                  <p className="text-xs text-gray-400 italic px-1.5">No items configured</p>
                )}
              </div>
            ))}

            {/* Notes */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-primary mb-1.5">Review Notes</p>
              <textarea
                className="w-full border rounded-lg p-2 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                rows={4}
                placeholder="Add internal notes…"
                value={notes}
                onChange={e => onNotesChange(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Col 2: Application Form (fixed width, scrollable) ── */}
        <div className="w-80 shrink-0 border-r overflow-y-auto bg-gray-50">
          <DrawerFormPanel app={app} extra={extra} />
        </div>

        {/* ── Col 3: Document Viewer (flex) ── */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <div className="flex border-b bg-gray-50 overflow-x-auto shrink-0">
            {availableDocs.length === 0 && (
              <span className="px-4 py-2 text-sm text-muted-foreground italic">No documents uploaded</span>
            )}
            {availableDocs.map((doc, idx) => (
              <button
                key={doc.key}
                onClick={() => onDocChange(idx)}
                className={`px-4 py-2.5 text-sm whitespace-nowrap border-r flex items-center gap-1.5 transition-colors ${docIndex === idx ? 'bg-white font-semibold text-primary border-b-2 border-b-primary' : 'text-muted-foreground hover:bg-white'}`}
              >
                {doc.isAdmin && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" title="Admin uploaded" />}
                {doc.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-hidden bg-gray-100">
            {!currentDoc ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                <FileText className="w-10 h-10 opacity-20" />
                <p className="text-sm">No documents to display</p>
              </div>
            ) : loadingDoc ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm">Loading document…</p>
              </div>
            ) : !resolvedSrc ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                <FileText className="w-10 h-10 opacity-20" />
                <p className="text-sm">Document could not be loaded</p>
              </div>
            ) : isImage ? (
              <div className="h-full flex items-center justify-center p-4 overflow-auto">
                <img src={resolvedSrc} alt={currentDoc.label} className="max-w-full max-h-full object-contain rounded shadow" />
              </div>
            ) : (
              <iframe src={resolvedSrc} title={currentDoc.label} className="w-full h-full border-0" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Application Form Panel (inside ReviewDrawer) ──────────────────────────────

function DrawerFormPanel({ app, extra }) {
  const [open, setOpen] = useState({
    personal: true, o_level: true, a_level: true, llb: true,
    statement: false, referees: false, declarations: false,
  });
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  const personal = extra?.personal_details || {};
  const oLevels  = extra?.o_levels || {};
  const aLevels  = extra?.a_levels || {};
  const llb      = extra?.llb || {};

  const Section = ({ id, title, badge, children }) => (
    <div className="border-b border-gray-200">
      <button
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-100 transition-colors"
        onClick={() => toggle(id)}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-600">{title}</span>
          {badge && <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold">{badge}</span>}
        </div>
        {open[id] ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
      </button>
      {open[id] && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );

  // Compact field: label on top, value below, optional highlight for key reconciliation fields
  const F = ({ label, value, highlight }) => (
    <div>
      <p className="text-xs text-gray-400 leading-tight">{label}</p>
      <p className={`text-xs font-medium leading-snug ${highlight ? 'text-primary font-semibold' : 'text-gray-800'} ${!value ? 'italic text-gray-400' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Sticky header */}
      <div className="px-4 py-2.5 bg-white border-b sticky top-0 z-10 shrink-0">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Application Form</p>
        <p className="text-xs text-muted-foreground">{app.full_name}</p>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto">

        <Section id="personal" title="Personal Information">
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <F label="Full Name" value={app.full_name} highlight />
            <F label="Date of Birth" value={app.date_of_birth} highlight />
            <F label="Age" value={extra?.age ? `${extra.age} years` : null} highlight />
            <F label="Nationality" value={app.nationality} />
            <F label="NIDA / Passport No." value={personal.nida_passport} highlight />
            <F label="ID Expiry" value={personal.id_expiry} />
            <F label="Place of Birth" value={personal.birth_place} />
            <F label="Phone" value={app.phone} />
            <F label="Email" value={app.email} />
            <F label="WhatsApp" value={personal.whatsapp} />
            <div className="col-span-2"><F label="Address" value={app.address} /></div>
            {app.currently_employed != null && (
              <div className="col-span-2">
                <F
                  label="Employment / Internship"
                  value={app.currently_employed
                    ? `Employed / Intern${app.employer_name ? ` — ${app.employer_name}` : ''}`
                    : 'Not employed / in internship'}
                />
              </div>
            )}
          </div>
        </Section>

        <Section id="o_level" title="O-Level Results" badge={oLevels.division}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <F label="School" value={oLevels.school} />
            <F label="Year" value={oLevels.year} />
            <div className="col-span-2"><F label="Division" value={oLevels.division} highlight /></div>
          </div>
        </Section>

        <Section id="a_level" title="A-Level Results" badge={aLevels.division || app.a_level_division}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3 mb-3">
            <F label="School" value={aLevels.school} />
            <F label="Year" value={aLevels.year} />
            <div className="col-span-2"><F label="Division" value={aLevels.division || app.a_level_division} highlight /></div>
          </div>
          {aLevels.subjects?.filter(s => s.subject).length > 0 && (
            <table className="w-full text-xs border rounded overflow-hidden">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold text-gray-500">Subject</th>
                  <th className="text-center px-2 py-1.5 font-semibold text-gray-500">Grade</th>
                </tr>
              </thead>
              <tbody>
                {aLevels.subjects.filter(s => s.subject).map((s, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">{s.subject}</td>
                    <td className="text-center px-2 py-1.5 font-bold text-primary">{s.grade || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {aLevels.no_a_level_details && (
            <p className="text-xs mt-2 text-gray-600"><strong>Alternative:</strong> {aLevels.no_a_level_details}</p>
          )}
        </Section>

        <Section id="llb" title="LLB Details" badge={app.llb_gpa ? `GPA ${app.llb_gpa}` : null}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
            <F label="Institute" value={llb.institute} />
            <F label="Status" value={app.llb_status} />
            <F label="CGPA" value={app.llb_gpa != null ? `${app.llb_gpa} / ${app.llb_gpa_scale || 5}.0` : null} highlight />
            <F label="Class" value={llb.class} />
            <F label="Graduation Date" value={llb.graduation_date} />
            <F label="Years Attended" value={llb.years_attended} />
          </div>
        </Section>

        <Section id="statement" title="Personal Statement">
          <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed bg-white rounded-lg border p-3 max-h-48 overflow-y-auto">
            {extra?.personal_statement || <span className="italic text-gray-400">No statement provided</span>}
          </div>
        </Section>

        <Section id="referees" title="Referees">
          {[
            { label: 'Referee 1', name: app.referee1_name, title: app.referee1_title, inst: app.referee1_institution, email: app.referee1_email, tel: app.referee1_telephone },
            { label: 'Referee 2', name: app.referee2_name, title: app.referee2_title, inst: app.referee2_institution, email: app.referee2_email, tel: app.referee2_telephone },
          ].map(r => (
            <div key={r.label} className="mb-3 last:mb-0 bg-white rounded-lg border p-3 space-y-2">
              <p className="text-xs font-semibold text-primary">{r.label}</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                <F label="Name" value={r.name} />
                <F label="Title" value={r.title} />
                <F label="Institution" value={r.inst} />
                <F label="Email" value={r.email} highlight />
                <div className="col-span-2"><F label="Telephone" value={r.tel} /></div>
              </div>
            </div>
          ))}
        </Section>

        <Section id="declarations" title="Declarations">
          <div className="space-y-2">
            {[
              { checked: app.declaration_info_true,      label: 'Information is true and accurate' },
              { checked: app.declaration_competitive,    label: 'Acknowledges competitive nature' },
              { checked: app.declaration_aptitude_tests, label: 'Willing to take Academic Assessment' },
              { checked: app.declaration_terms_agreed,   label: 'Agreed to Terms & Conditions' },
            ].map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${d.checked ? 'bg-green-100' : 'bg-red-50'}`}>
                  {d.checked
                    ? <CheckCircle className="w-3 h-3 text-green-600" />
                    : <XCircle className="w-3 h-3 text-red-500" />}
                </div>
                <span className="text-gray-700">{d.label}</span>
              </div>
            ))}
            <p className="text-xs text-gray-400 pt-1">Signed: {app.declaration_date || 'N/A'}</p>
          </div>
        </Section>

      </div>
    </div>
  );
}

function ScoreBar({ label, sub, value, max, color, detail }) {
  return (
    <div className="rounded-lg border bg-gray-50 p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-sm">{label} <span className="font-normal text-muted-foreground text-xs">({sub})</span></span>
        <span className="font-bold text-primary text-sm">{value.toFixed(1)} / {max}</span>
      </div>
      {detail && <p className="text-xs text-muted-foreground mb-2">{detail}</p>}
      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${(value / max) * 100}%` }} />
      </div>
    </div>
  );
}
